/**
 * Reducer for the casemedia (folder view) state module.
 *
 * Exports caseMediaReducer, a Redux Toolkit createReducer keyed by the module namespace
 * from ../store. It maintains the cases/folders/files tree plus selection (including the
 * Ctrl/Shift-click multi-file selection and in-flight batch-operation progress),
 * pagination (a root-folders response at offset 0 full-replaces the list, so its stored
 * offset names the last page it covers rather than the offset it asked for — that is
 * what Load more advances from), search state and scope, move-folder/move-file state,
 * legacy media details (the redact-state SDOs plus their enriched TDOs, from which the
 * synthetic Unassigned Media folder is derived — VE-26943),
 * export and download-all status, file filters, the list/grid file view mode, the
 * folder-tree fetch lifecycle (drives the restore loading pane), last
 * active/opened file tracking used to restore scroll position, per-folder OLP
 * verdicts (folderPermissions — update/move/delete tracked separately per folder),
 * and the pending-trash cover (pendingTrashFolderIds) that keeps a folder whose move
 * into Trash is still in flight out of every root refetch until the move settles.
 * It also patches a row's output assets, the redaction-data assets that judge them, and
 * the flat outputFiles list in place from a targeted refresh (refreshFileOutputsSuccess —
 * VE-28061) rather than refetching the folder, plus the two row fields an editor session can
 * change safely — name and modifiedDateTime. A document burn-in's EXPORT_COMPLETED records the
 * new output on its row directly (redactedDocumentExported — VE-30011), because no refresh
 * runs on the editor route and the read that follows can miss a just-created asset — and it
 * carries the same news to the REQUEST ROSTER, which is not a row store and which
 * forEachRowStore therefore does not reach (patchRosterHasRedactedOutput); deleting
 * that output takes it off the rows the same way (redactedDocumentDeleted), which a refresh
 * cannot do either. A document-editor rename is applied the same way (documentRenamed —
 * VE-31012), and is the ONLY writer that reaches inside `details`, where it touches just
 * the file's name copies (`name`, `veritoneFile.filename` / `fileName`).
 * Refreshes never write `status`, whose recorded override
 * (VE-23391) a re-read would undo, and never `details`: its redaction-status tags are
 * written optimistically by the folder view, so a refresh landing first would stamp the old
 * status back and look like a failed save (VE-28429). Case-contents reads retain the list
 * in caseContentsRows (and stamp caseContentsLoadedAt, the record of when it came off the
 * server), which lets a re-select put the rows straight back and lets the saga skip the
 * refetch entirely. Folder-listing reads do the same with subFoldersRows /
 * subFoldersLoadedAt, so the column one level out from a request survives an editor round
 * trip too.
 * Re-selecting a folder whose rows we still hold keeps them rather than emptying the
 * list (VE-28429) — both editors re-select on their way out, and those rows are what the
 * folder view renders from, so resetting the selection blanked it behind a spinner for
 * the whole route transition. Only that folder's OWN rows are ever reused, and only
 * under the same rule the fetch skip uses: VE-30022 made that rule the TRIP away
 * (folderViewTrip, stamped on entering an editor from anywhere that is not another
 * editor — how long we have been gone, and which columns were on screen) rather than
 * the list's own age, which expired while the user
 * was still sitting in the request and reloaded a request after a ten-second look at one
 * of its documents.
 * It also holds the open request's ROSTER (requestRoster — VE-30449, widened by
 * VE-32420): one cheap entry per file carrying what the list sorts, filters and selects
 * by, which is why it is no longer the names-only snapshot the upload duplicate-name
 * check originally asked for — that check now rides an index the list owns. One request
 * at a time, and a response is applied only if it answers that request's latest read.
 * Individual case handlers are also exported by name so they can be
 * reused and unit tested directly. Actions come from ../actions; sagas live in ../sagas.
 *
 * `mapMediaFiles` additionally drops a request's MERGED DOCUMENT TDO, which
 * holds combined redacted output rather than a source file and must never present as
 * one. That filter is written once because `caseFiles.inputFiles` is the single source
 * for the folder column, the editor's Documents panel, the media drawer and the grid —
 * but it is NOT the only way such a TDO can surface, and anything reading `childTDOs`
 * directly needs its own guard. The folder's content templates are kept here too, since
 * they carry the pointer to that TDO.
 */
import { createReducer } from '@reduxjs/toolkit';
// Deep import, not the documents barrel: the barrel pulls the documents sagas,
// which import casemedia — a cycle.
import {
  EXPORT_COMPLETED,
  DELETE_REDACTED_DOCUMENT_SUCCESS,
  RENAME_DOCUMENT_SUCCESS,
  SAVE_SUCCESS,
} from '@redact-modules/documents/actions';
import {
  FETCH_ROOT_FOLDERS_INIT,
  FETCH_ROOT_FOLDERS_SUCCESS,
  FETCH_ROOT_FOLDERS_FAILURE,
  SET_ROOT_FOLDER_SMART_SELECTION,
  SET_SELECTED_FOLDER_REQUEST,
  FETCH_SUB_FOLDERS_BYID_REQUEST,
  FETCH_SUB_FOLDERS_BYID_SUCCESS,
  FETCH_SUB_FOLDERS_BYID_FAILURE,
  SET_SELECTED_VIRTUAL_FOLDER_REQUEST,
  DELETE_FOLDER_INIT,
  DELETE_FOLDER_SUCCESS,
  DELETE_FOLDER_FAILURE,
  DELETE_FILE_INIT,
  DELETE_FILE_SUCCESS,
  DELETE_MEDIA_FILE_SDO_SUCCESS,
  DELETE_FILE_FAILURE,
  FETCH_EXPORT_TDO_SUCCESS,
  SET_DOWNLOAD_ALL_STATUS,
  FETCH_MORE_ROOTFOLDERS_INIT,
  FETCH_MORE_ROOTFOLDERS_SUCCESS,
  FETCH_MORE_ROOTFOLDERS_FAILURE,
  FETCH_MORE_SUBFOLDERS_INIT,
  FETCH_MORE_SUBFOLDERS_SUCCESS,
  FETCH_MORE_SUBFOLDERS_FAILURE,
  MOVE_FOLDER_SUCCESS,
  REMOVE_SELECTED_FOLDER,
  FETCH_SELECTED_CASE_OR_FOLDER_TREE_SUCCESS,
  CLEAR_NAV_CASE,
  MARK_FOLDER_VIEW_LEFT,
  SET_EDITOR_RETURN_TARGET,
  CLEAR_ALL_SELECTED_FOLDERS,
  CLEAR_LAST_ACTIVE_FOLDER_VIEW_FILE,
  SET_SEARCH_TEXT,
  SET_FOLDER_PERMISSIONS,
  SET_SEARCH_RESULTS,
  RESTORE_SEARCH_RESULTS,
  FETCH_MORE_FILES_INIT,
  FETCH_MORE_FILES_SUCCESS,
  FETCH_MORE_FILES_FAILURE,
  CLEAR_ALL_SEARCH_RESULTS,
  SET_SEARCH_MORE_RESULTS,
  SET_SEARCH_SCOPE,
  CLEAR_SEARCH_SCOPE,
  SET_SEARCH_BAR_SCOPE_MODE,
  OPEN_SEARCH_DIALOG,
  CLOSE_SEARCH_DIALOG,
  SEARCH_CASES_AND_FOLDERS_INIT,
  SEARCH_CASES_AND_FOLDERS_FAILURE,
  SET_SHOW_LEGACY_MEDIAS,
  FETCH_LEGACY_MEDIAS_INIT,
  FETCH_LEGACY_MEDIAS_FAILURE,
  FETCH_TDO_DETAIL_FOR_SDOS_SUCCESS,
  FETCH_LEGACY_MEDIAS_SUCCESS,
  FETCH_TDO_DETAIL_FOR_SDOS_FAILURE,
  RENAME_FOLDER_SUCCESS,
  SET_MOVE_EXPANDED_FOLDER_REQUEST,
  SET_SOURCE_FOLDER_TO_MOVE_REQUEST,
  CLEAR_ALL_MOVE_FOLDER_STATE_REQUEST,
  CLEAR_FROM_MOVE_EXPANDED_REQUEST,
  FETCH_FOR_EXPANDED_FOLDER_INIT,
  FETCH_FOR_EXPANDED_FOLDER_SUCCESS,
  FETCH_FOR_EXPANDED_FOLDER_FAILURE,
  SET_SOURCE_FILE_TO_MOVE_REQUEST,
  FILE_TDO_WITH_CASE_SUCCESS,
  SET_SELECTED_REQUEST_TO_INGEST,
  CLEAR_SELECTED_REQUEST_TO_INGEST,
  CREATE_ROOT_FOLDER_FAILURE,
  MARK_TDO_RECORDED,
  MARK_FOLDER_VIEW_TDO_OPENED,
  SET_CASE_MEDIA_STATUS,
  CONSUME_FOLDER_VIEW_RETURN_SCROLL,
  CLEAR_LAST_OPENED_FOLDER_VIEW_FILE_FOR_CASE,
  UPDATE_REDACTION_STATUS_CHANGE,
  SET_LAST_ACTIVE_FOLDER_VIEW_FILE,
  SET_SOURCE_INPUT_FILE_TO_MOVE,
  SOFT_DELETE_FOLDER_SUCCESS,
  SOFT_DELETE_FOLDER_FAILURE,
  CHANGE_FILE_FILTERS,
  CHANGE_FILE_VIEW_MODE,
  SET_SIDEBAR_COLLAPSED,
  FETCH_SELECTED_CASE_OR_FOLDER_TREE_INIT,
  FETCH_SELECTED_CASE_OR_FOLDER_TREE_FAILURE,
  SET_DOWNLOAD_ALL_FOR_TDO,
  TOGGLE_FILE_MULTI_SELECT,
  SELECT_FILE_MULTI_RANGE,
  CLEAR_FILE_MULTI_SELECT,
  SET_PENDING_REMOVAL_FILES,
  RELEASE_PENDING_REMOVAL_FILES,
  CLEAR_PENDING_REMOVAL_FILES,
  RELEASE_PENDING_TRASH_FOLDERS,
  REMOVE_CASE_INPUT_FILES,
  SET_BATCH_OPERATION,
  REFRESH_FILE_OUTPUTS_SUCCESS,
  FETCH_REQUEST_ROSTER_REQUEST,
  SET_REQUEST_ROSTER,
  SET_UNASSIGNED_SORT,
  PUBLISH_REQUEST_ROSTER,
  APPEND_REQUEST_ROSTER,
  MERGE_CASE_FILE_ROWS,
} from '@redact-modules/casemedia/actions';
import {
  AssetFileType,
  BatchOperationState,
  CaseFilesType,
  CaseMediaState,
  RetainedSubFolderListing,
  defaultState,
  ExportedTdoState,
  FileViewMode,
  FolderPermissionFlags,
  FolderStateType,
  LegacyMediaDetailType,
  namespace,
  SearchResultFile,
  SearchResultFolder,
  UnassignedTdoRecord,
  VirtualFolderNames,
  VirtualFolderStateType,
  UnassignedSort,
} from '@redact-modules/casemedia/store';
import get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { cloneDeep, isEmpty } from 'lodash';
import { ROUTE_HOME } from '@common/state/modules/routing';
import {
  CaseId,
  FolderId,
  TDOId,
  TreeObjectId,
} from '@common-modules/universal/models/Brands';
import {
  ActionChangeFileFiltersPayload,
  ActionDeleteFilePayload,
  ActionDownloadAllForTdoPayload,
  ActionDownloadAllStatusPayload,
  ActionFetchForExportedFolderPayload,
  ActionFetchLegacyMediasRequest,
  ActionFetchMoreFilesRequest,
  ActionFetchMoreSubFoldersRequest,
  ActionLegacyMediasSuccessPayload,
  ActionNavToCaseMediaPayload,
  ActionSetLastActiveFolderViewFilePayload,
  ActionRenameFolderPayload,
  ActionSetCaseMedia,
  ActionSetMoveInputFilePayload,
  ActionSetShowLegacyMediasPayload,
  ActionSetSourceFileToMovePayload,
  ActionUpdateRedactionStatusChangePayload,
} from '@redact-modules/casemedia/models';
import _sortBy from 'lodash/sortBy';
import type { TemporalDataObject } from 'veritone-types';
import { OrganizationPayload } from '@common/user-onboarding/models/organization';
import { REQUEST_USER_ORGANIZATION_SUCCESS } from '@common/user-onboarding/actions/organization';
import { SYSTEM_FOLDER_REDACT_TRASH } from '@redact-modules/trash/constants';
import { selectAdminRootFolder, sortFolderPath } from '@redact-modules/helper';
import { defaultLimit } from '@redact-modules/casemedia/selectors';
import { isRequestInputFile } from '@redact-modules/casemedia/requestMembership';
import {
  canReuseAfterTrip,
  ILLUMINATE_APP_ID,
  ILLUMINATE_FOLDERNAME,
} from '../contants';
import { checkIsCaseId } from '@utils';
import { isUnassignedFolderId } from '@redact-modules/casemedia/unassigned';
import {
  CREATE_PROJECT_SUCCESS,
  DELETE_PROJECT_SUCCESS,
} from '@redact-modules/projects/actions';
import type { ProjectPointer } from '@redact-modules/projects/models';
import {
  pickCaseContentTemplate,
  pickProjectsContentTemplate,
} from '@redact-modules/casemedia/contentTemplates';
import { IN_REDACTION_TAG_KEY } from '@common-modules/mediaDetails';
import type {
  StalenessAssetRecord,
  StalenessAssetsResponse,
  StalenessOutputRecord,
} from '@redact-modules/casemedia/services/queries';
import {
  getBuiltInRedactionStatusTagWriteFields,
  mergeRedactionStatusTagFields,
} from '@common-modules/mediaDetails/redactionStatuses';
interface Action<P, M> {
  payload: P;
  meta: M;
}

const fetchRootFolderInit = (state: CaseMediaState): CaseMediaState => ({
  ...state,
  rootFolder: {
    ...(state.rootFolder || {}),
    fetchingSubFolders: true,
  },
});

// Which of the returned roots this org's content lives under. `smartSelection`
// (the `rootFolderSmartSelection` app-config flag, mirrored into this slice by
// the fetch sagas) breaks the tie by child-folder count when more than one
// owner-less root comes back; without it the first owner-less root wins.
const getRootAdminFolder = (
  state: CaseMediaState,
  rootFolders: FolderStateType[]
) =>
  selectAdminRootFolder(rootFolders, {
    smartSelection: state.rootFolderSmartSelection,
  }) ?? null;

const setRootFolderSmartSelection = (
  state: CaseMediaState,
  action: { payload: boolean }
): CaseMediaState => ({
  ...state,
  rootFolderSmartSelection: action.payload,
});

const filterSystemFolders = (f: FolderStateType) =>
  f.name !== SYSTEM_FOLDER_REDACT_TRASH;

// VE-26484: a folder whose move into Trash is confirmed but not yet committed
// is still returned by the server under its old parent, so any root refetch
// landing in that window would resurrect it. Match on both keys for the same
// reason softDeleteFolderSuccess does: v1 flows key by treeObjectId, v2 by
// folder id, and root records carry both.
const isPendingTrash = (
  pendingTrashFolderIds: readonly TreeObjectId[],
  folder: FolderStateType
) =>
  pendingTrashFolderIds.some(
    (pendingId) =>
      folder.treeObjectId === pendingId ||
      String(folder.id) === String(pendingId)
  );

const fetchRootFolderSuccess = (
  state: CaseMediaState,
  action: Action<
    { rootFolders: FolderStateType[] },
    { variables: { limit: number; offset: number } } | undefined
  >
): CaseMediaState => {
  if (!action.meta) {
    return state;
  }
  const { limit, offset } = action.meta.variables;
  const rootFolder = getRootAdminFolder(
    state,
    get(action, 'payload.rootFolders', [])
  );
  if (rootFolder) {
    return {
      ...state,
      rootFolderId: rootFolder.id || '',
      rootFolder: { ...mapRootFolder(state, rootFolder, limit, offset) },
    };
  }
  return { ...state };
};

const fetchRootFolderFailure = (state: CaseMediaState): CaseMediaState => ({
  ...state,
  rootFolder: {
    ...(state.rootFolder || {}),
    fetchingSubFolders: false,
  },
  // A pending last-open restore can never proceed without the root folder —
  // mark the tree lifecycle failed so CaseFolders drops its restore loading
  // pane and shows the normal (root) view instead of spinning forever.
  folderTreeFetchState: 'failed',
});
const filteroutIlluminateApp = (
  state: CaseMediaState,
  folders: FolderStateType[]
) =>
  state.isIlluminateAppEnabled
    ? folders.filter((f) => f.name !== ILLUMINATE_FOLDERNAME)
    : [...folders];

const mapRootFolder = (
  state: CaseMediaState,
  data: FolderStateType,
  limit: number,
  offset: number
): FolderStateType => {
  const existingFolders = state?.rootFolder?.subfolders || [];
  const pendingTrashFolderIds = state?.pendingTrashFolderIds || [];
  const newListFolders = filteroutIlluminateApp(
    state,
    get(data, 'childFolders.records', [])
  )
    .filter(filterSystemFolders)
    // VE-26484: drop folders whose trash move is still in flight, whichever
    // refetch this is — otherwise creating/renaming a folder during that window
    // brings the trashed one back as a briefcase-icon ghost.
    .filter((f: FolderStateType) => !isPendingTrash(pendingTrashFolderIds, f));

  // remove duplicates - this fixes a platform bug - consider removing later or can keep as a safeguard
  let subfolders =
    offset === 0
      ? [...newListFolders]
      : [...existingFolders, ...newListFolders];

  subfolders = [...new Map(subfolders.map((item) => [item.id, item])).values()];

  // VE-26833: `fetchSubFolderOffset` is what Load more advances from
  // (`rootFoldersOffset + defaultLimit`), so it has to name the last page this
  // response covers — not the request's offset. Those differ only for the
  // delete/trash reconciling refetch, which collapses every loaded page into one
  // request at offset 0 (limit 60, 90, ...). Storing 0 there would send the next
  // Load more back to offset 30 for a page already on screen, and
  // mapFetchMoreRootFolders appends without deduping.
  const pageSize = state.defaultFetchMoreLimit || defaultLimit;
  const lastPageOffset = offset === 0 ? Math.max(0, limit - pageSize) : offset;

  return {
    ...data,
    id: data.id,
    name: data.name,
    treeObjectId: data.treeObjectId,
    subfolders,
    fetchingSubFolders: false,
    fetchSubFolderLimit: limit,
    fetchSubFolderOffset: lastPageOffset,
    fetchMoreAvailable: (data.childFolders?.records?.length || 0) === limit,
  };
};

const mapSortedFolders = (folders: FolderStateType[]) =>
  _sortBy(folders || [], [
    function (f) {
      return f.name?.toLowerCase();
    },
  ]);

const setSelectedFolderRequest = (
  state: CaseMediaState,
  // action: Action<{ folder: FolderStateType }, never>
  action: {
    payload: {
      folder: FolderStateType;
    };
  }
) => {
  const nextFolder = action.payload?.folder;
  const nextCaseId =
    nextFolder &&
    (checkIsCaseId(nextFolder) || isUnassignedFolderId(nextFolder.id))
      ? nextFolder.id
      : undefined;
  /**
   * VE-28429: the rows we already hold for the folder being selected, wherever
   * they currently live.
   *
   * Three sources, most authoritative first: the incoming node itself; the
   * virtual folder's parent when it IS this folder; and the selected-folders
   * chain, which `mapSelectedFolders` carries forward untouched for every
   * ancestor shallower than the current selection.
   *
   * The chain is the one that matters on the way back from an editor. Returning
   * replays the whole ancestor path, and each node arrives as a bare tree node
   * with no `caseFiles`. Selecting a deeper folder legitimately drops the
   * virtual parent, so by the time the request itself comes round again there is
   * nothing held to compare it against — yet the chain still has the request's
   * loaded rows sitting right there.
   */
  const chainEntry = nextFolder?.id
    ? (state.selectedFolders || []).find((f) => f?.id === nextFolder.id)
    : undefined;
  const heldParent =
    nextFolder?.id && state.selectedVirtualFolder?.parent?.id === nextFolder.id
      ? state.selectedVirtualFolder.parent
      : undefined;
  /**
   * The retained copy is only usable while we are coming back from a short trip
   * away, and only for a column that trip was showing. The saga's fetch skip
   * already checks this; without the same check here the reducer would paint an
   * arbitrarily old snapshot onto the selection and merely rely on a refetch to
   * correct it — which leaves stale rows on screen if that refetch fails, and
   * shows files that have since been trashed or moved. One rule governs both
   * halves (`canReuseAfterTrip`).
   *
   * VE-30022: that rule used to read `caseContentsLoadedAt`, i.e. the list's own
   * age, which expired while the user was still sitting in the request and made
   * even a ten-second look at a document cost a reload.
   */
  const reusableId =
    nextFolder?.id && canReuseAfterTrip(state.folderViewTrip, nextFolder.id)
      ? nextFolder.id
      : undefined;
  const retainedRows = reusableId
    ? state.caseContentsRows?.[reusableId]
    : undefined;
  /**
   * The same for a plain folder's subfolder listing — the column one level out
   * from a request. Outside the trip the node arrives bare and the listing is
   * fetched as before. The live chain entry is preferred to the copy: it carries
   * any Load-more pages and in-place edits.
   */
  const heldListing =
    reusableId && !nextFolder?.subfolders
      ? (pickSubFolderListing(chainEntry) ?? state.subFoldersRows?.[reusableId])
      : undefined;
  /**
   * Only `caseFiles` and (inside the trip) the subfolder listing are
   * recovered — never `folderPath`. Merging the held objects wholesale would
   * back-fill `folderPath` onto a bare tree node, and `mapSelectedFolders`
   * branches on it, so that silently changed the column-stack semantics of
   * every re-select, not just the editor exit this exists for.
   */
  const enrichedNext = nextFolder
    ? ({
        ...nextFolder,
        ...heldListing,
        caseFiles:
          (nextFolder as FolderStateType<CaseId>)?.caseFiles ??
          heldParent?.caseFiles ??
          chainEntry?.caseFiles ??
          retainedRows,
      } as FolderStateType<CaseId>)
    : undefined;

  /**
   * Keep the selection when we hold this folder's rows — which is what the
   * media editor does on every exit, and (since this ticket) the document
   * editor too.
   *
   * FolderColumn renders `selectedVirtualFolder.parent.caseFiles.inputFiles` and
   * nothing else, so resetting to `{}` here empties the list instantly and only
   * a contents fetch refills it. On the common path that is seconds away:
   * `caseMediaSaga` is cancelled while an editor route is up, and
   * `warmCaseTreeWatchers` is forked only when the editor's folder was NOT
   * already selected (see caseDiscovery) — opening a file from the folder you
   * are already in is exactly the case it skips. The folder view came back blank
   * behind a spinner for the whole route transition and refetch.
   *
   * Only folders whose rows we actually hold qualify, so moving to a folder we
   * have never loaded still resets: showing one folder's files under another
   * folder's name would be worse than a spinner. Reusing a folder's OWN
   * previously loaded rows is not that — it is the correct list, briefly stale,
   * and the incoming fetch replaces it.
   */
  const canKeepRows = !!enrichedNext?.caseFiles;
  const updatedState: CaseMediaState = {
    ...state,
    // VE-26943: Unassigned Media behaves like a request whose files are already
    // in state, so point the virtual input folder at it immediately — there is
    // no contents fetch whose success would otherwise do this (see
    // fetchSubFoldersByIdRequest). Only the sentinel id is stored; the selectors
    // substitute the freshly derived folder on read.
    selectedVirtualFolder: isUnassignedFolderId(nextFolder?.id)
      ? {
          parent: nextFolder as FolderStateType<CaseId>,
          virtualFolderName: VirtualFolderNames.INPUT,
        }
      : canKeepRows
        ? {
            ...state.selectedVirtualFolder,
            parent: enrichedNext,
            // A re-select always landed on Input before this change (the reset
            // sent `mapFilesFetchStatus` down its rebuild branch, which hardcodes
            // it). Keep that, so only the blanking behaviour differs.
            virtualFolderName: VirtualFolderNames.INPUT,
          }
        : {},
    // The enriched node, so the chain keeps the rows it is carrying rather than
    // having them overwritten by the bare node that arrived — the chain is
    // where they are recovered from next time round.
    selectedFolders: mapSelectedFolders(
      enrichedNext ?? nextFolder,
      state.selectedFolders
    ),
  };
  if (state.lastActiveFolderViewFile) {
    const storedCaseId = state.lastActiveFolderViewFile.caseId;
    if (!nextCaseId || nextCaseId !== storedCaseId) {
      updatedState.lastActiveFolderViewFile = null;
    }
  }
  /**
   * VE-28429: keep the retained copy tracking the live list.
   *
   * It is written when a request's contents load, which makes it a snapshot of
   * how the list looked THEN. Anything that edits a row afterwards — a status
   * changed inside an editor being the one that matters here, since the folder
   * view is not even on screen for it — would otherwise be undone the moment
   * the restore puts the stale copy back: the row showed the new status, then
   * reverted to the old one.
   *
   * `enrichedNext.caseFiles` is the freshest list available at this point (the
   * incoming node, the held parent, then the chain, and only then the retained
   * copy itself), so writing it back either advances the copy or is a no-op.
   */
  if (enrichedNext?.id && enrichedNext.caseFiles) {
    updatedState.caseContentsRows = {
      ...state.caseContentsRows,
      [enrichedNext.id]: enrichedNext.caseFiles,
    };
  }
  if (nextFolder?.id && heldListing) {
    updatedState.subFoldersRows = {
      ...state.subFoldersRows,
      [nextFolder.id]: heldListing,
    };
  }
  return updatedState;
};

const pickSubFolderListing = (
  folder: FolderStateType | undefined
): RetainedSubFolderListing | undefined =>
  folder?.subfolders
    ? {
        subfolders: folder.subfolders,
        fetchSubFolderLimit: folder.fetchSubFolderLimit,
        fetchSubFolderOffset: folder.fetchSubFolderOffset,
        fetchMoreAvailable: folder.fetchMoreAvailable,
      }
    : undefined;

const mapSelectedFolders = (
  folder: FolderStateType,
  selectedFolders: FolderStateType[]
) => {
  if (folder?.folderPath?.length) {
    const selectedLevel = folder?.folderPath?.length;
    if (selectedFolders && selectedFolders.length > 0) {
      const filteredFolders = selectedFolders.filter((item) => {
        if (item?.folderPath?.length) {
          return item.folderPath.length < selectedLevel;
        }
      });
      return [...filteredFolders, folder];
    }
  }
  return [folder];
};

const fetchSubFoldersByIdRequest = (
  state: CaseMediaState,
  action: Action<any, { variables: { caseId?: CaseId; folderId?: FolderId } }>
) => {
  const request = action.meta.variables;
  const isCase = !_isEmpty(request.caseId);
  const selectedId = isCase ? request.caseId : request.folderId;
  if (selectedId) {
    state.selectedFolders = mapFetchingSubFolderStatus(
      selectedId,
      state.selectedFolders
    );
    state.selectedVirtualFolder = mapFilesFetchStatus(
      state,
      isCase,
      true,
      selectedId
    );
    return;
    // return updatedState;
  }
  return { ...state };
};

const mapFetchingSubFolderStatus = (
  selectedId: FolderId | undefined,
  selectedFolders: FolderStateType[]
) =>
  (selectedFolders || []).map((folder: FolderStateType) => {
    if (folder?.id === selectedId) {
      folder.fetchingSubFolders = true;
    }
    return folder;
  });

const fetchSubFoldersByIdSuccess = (
  state: CaseMediaState,
  action: Action<
    { folder: FolderStateType },
    {
      variables: {
        caseId?: CaseId;
        folderId?: FolderId;
        limit: number;
        offset: number;
      };
    }
  >
) => {
  const request = action.meta.variables;
  const isCase = !_isEmpty(request.caseId);
  // TODO: the case here isn't exactly correct since caseId and folderId are mutually exclusive
  const selectedId = (isCase ? request.caseId : request.folderId)!;
  // VE-28429: only page one is stamped, so a partial load can never date the
  // whole request. Defensive rather than load-bearing today: paging runs through
  // its own FETCH_MORE_FILES_* family and does not reach this reducer, and every
  // dispatcher of the selection actions passes offset 0. Kept because the stamp's
  // meaning — "the whole list landed at this time" — is what the skip relies on,
  // and a future caller reusing this action at an offset would quietly break it.
  if (isCase && !request.offset) {
    state.caseContentsLoadedAt[selectedId] = Date.now();
  }
  state.selectedFolders = mapSelectedWithSubFolders(
    selectedId,
    state.selectedFolders,
    action,
    !_isEmpty(request.caseId)
  );
  /**
   * VE-28429: keep the list itself, not just when it landed.
   *
   * Read from `selectedFolders` rather than the action: `mapSelectedWithSubFolders`
   * above is what assembles `caseFiles` out of the response, and
   * `refreshSelectedVirtualFolder` below reads it straight back out of there —
   * the raw payload does not carry it in this shape.
   *
   * Returning from an editor re-selects this request, and the document editor's
   * exit goes through the folder-tree restore, which re-selects every node in
   * the path as a bare server node. By the time the request itself comes round
   * again, neither the virtual folder nor the chain still holds its files — the
   * chain because `mapSelectedFolders` replaces it wholesale when the incoming
   * node has no `folderPath`. This copy is keyed by request id and survives all
   * of that; `caseContentsLoadedAt` bounds how long it may be reused.
   */
  if (isCase && !request.offset) {
    const loadedRows = (state.selectedFolders || []).find(
      (folder) => folder?.id === selectedId
    )?.caseFiles;
    if (loadedRows) {
      state.caseContentsRows[selectedId] = loadedRows;
    }
  }
  if (!isCase && !request.offset) {
    const listing = pickSubFolderListing(
      (state.selectedFolders || []).find((folder) => folder?.id === selectedId)
    );
    if (listing) {
      state.subFoldersLoadedAt = {
        ...state.subFoldersLoadedAt,
        [selectedId]: Date.now(),
      };
      state.subFoldersRows = { ...state.subFoldersRows, [selectedId]: listing };
    }
  }
  state.selectedVirtualFolder = refreshSelectedVirtualFolder(state, isCase);
};

const refreshSelectedVirtualFolder = (
  state: CaseMediaState,
  isCase: boolean
) => {
  const virtualFolder = state.selectedVirtualFolder;
  if (isCase && virtualFolder?.virtualFolderName) {
    const updatedVirtualFolder: VirtualFolderStateType = {
      ...state.selectedVirtualFolder,
      fetchingFiles: false,
      parent: {
        ...((state.selectedFolders || [])?.filter(
          (folder: FolderStateType): folder is FolderStateType<CaseId> =>
            folder.id === virtualFolder?.parent?.id
        )?.[0] || {}),
      },
    };
    return updatedVirtualFolder;
  }
  return { ...state.selectedVirtualFolder };
};

export const setCaseMediaStatus = (
  state: CaseMediaState,
  data: {
    payload: ActionSetCaseMedia;
  }
) => {
  const caseFiles = state.selectedVirtualFolder.parent?.caseFiles;
  const fileInputUpdate: AssetFileType[] = [];
  caseFiles?.inputFiles?.forEach((file) => {
    if (file.id === data.payload.tdoId) {
      const updateFile = file;
      updateFile.jobs = {
        count: 1,
        records: [{ status: data.payload.status }],
      };
      fileInputUpdate.push(updateFile);
    } else {
      fileInputUpdate.push(file);
    }
  });
  state.selectedVirtualFolder.parent = {
    ...state.selectedVirtualFolder.parent,
    caseFiles: {
      ...state.selectedVirtualFolder.parent?.caseFiles,
      inputFiles: fileInputUpdate,
    },
  };
  return;
};

/**
 * VE-28061: bring a row up to date after a redaction finishes, without
 * refetching the whole folder.
 *
 * Overwrites the row's output assets, the redaction-data assets the staleness
 * verdict compares them against, and the engine task list the "Redacting" pill
 * reads — all from one fresh read (`fetchStalenessAssetsQuery`, whose selections
 * match the folder load's), so the pill and Download Review describe the file
 * now rather than at page load.
 *
 * Patches every place a row for that TDO can live: the selected column chain,
 * the virtual-folder input list, the flat `outputFiles` list the download paths
 * read, and the Unassigned Media TDO map the synthetic folder is derived from.
 *
 * Three rules keep a patched row indistinguishable from a freshly loaded one,
 * because a half-patched row is a state no loader produces and no consumer
 * expects:
 *
 * 1. **Outputs move with their judges.** `redactionDataAssets` /
 *    `redactionSelectionAssets` are patched alongside the outputs. Refreshing
 *    only the outputs left a fresh output beside a page-load save label, which
 *    can read NOT-stale for a file that is genuinely stale.
 * 2. **The flat `outputFiles` list is rebuilt.** `handleDownloadAllOutputFiles`
 *    gates on that list, so a row that flipped to "Redacted" while the list
 *    still held nothing made Download All Output Files return silently.
 * 3. **An empty result is "no answer", not "nothing".** An empty `records`
 *    array is also what a read-your-writes miss returns moments after
 *    `createAsset`, and treating it as authoritative turned a correctly
 *    "Redacted" row back into "Original". Outputs the row already has win.
 *
 * A TDO the query returned nothing for (deleted meanwhile) is left as it was.
 */
export const refreshFileOutputsSuccess = (
  state: CaseMediaState,
  { payload }: { payload: StalenessAssetsResponse }
) => {
  const fresh = Object.values(payload ?? {}).filter(
    (record): record is NonNullable<StalenessAssetsResponse[string]> =>
      !!record?.id
  );
  if (fresh.length === 0) {
    return;
  }
  const byTdoId = new Map(fresh.map((record) => [record.id, record]));
  const hasOutputs = (record: NonNullable<StalenessAssetsResponse[string]>) =>
    (record.redactedMediaAssets?.records?.length ?? 0) > 0;
  /**
   * A redaction-data record with no timestamp normalises to `''` rather than
   * being dropped — the staleness pickers skip what they cannot parse, which is
   * the same "unknown". Matches `judgeFiles`' handling in FolderColumn.
   */
  const dated = (records: StalenessAssetRecord[] | undefined) =>
    records && {
      records: records.map((record) => ({
        ...record,
        createdDateTime: record.createdDateTime ?? '',
      })),
    };

  const patchRow = (row: AssetFileType) => {
    const record = byTdoId.get(row.id);
    if (!record) {
      return;
    }
    // Output records carry their parent so the download resolver never has to
    // fall back to the id's shape (VE-26943).
    const outputs = (records: StalenessOutputRecord[] | undefined) =>
      records && {
        records: records.map((output) => ({
          ...output,
          parentTdoId: row.id,
        })) as unknown as AssetFileType[],
      };
    if (record.tasks) {
      row.tasks = record.tasks;
    }
    // VE-28429: and the jobs, which is what the processing dot to the left of
    // the filename actually reads. That dot is hidden while the redact engine
    // runs, so a row carrying its redaction job as `queued` looks fine right up
    // until the "Redacting" pill clears — and then shows a stale
    // "Cognition Queued/Pending" for the rest of the session, because nothing
    // else re-reads jobs.
    if (record.jobs) {
      row.jobs = record.jobs as AssetFileType['jobs'];
    }
    // VE-28429: the editor-changeable row fields, so a targeted refresh of the
    // file just closed can stand in for reloading the whole request.
    //
    // Deliberately only these two. `status` is left alone because the recorded
    // override (VE-23391) lives there and a re-read can downgrade a just-recorded
    // file — and `details.tags`, which carries the redaction-status pill, is left
    // alone for exactly the same reason: the folder view writes a status change
    // optimistically, so a refresh landing before that write is readable would
    // stamp the old status back and look like a failed save. Status still
    // reaches the row through the ordinary folder refetch; a wrong value is
    // worse than a late one. (The narrow `'tags' in ...` form also could not
    // represent a REMOVED tag, so it was never a complete answer either.)
    if (record.name) {
      row.name = record.name;
    }
    if (record.modifiedDateTime) {
      row.modifiedDateTime = record.modifiedDateTime;
    }
    // Rule 3.
    const keepExistingOutputs =
      !hasOutputs(record) &&
      (row.redactedMediaAssets?.records?.length ?? 0) > 0;
    if (!keepExistingOutputs) {
      row.redactedMediaAssets =
        outputs(record.redactedMediaAssets?.records) ?? row.redactedMediaAssets;
      row.auditLogAssets =
        outputs(record.auditLogAssets?.records) ?? row.auditLogAssets;
      row.redactedTranscriptAssets =
        outputs(record.redactedTranscriptAssets?.records) ??
        row.redactedTranscriptAssets;
    }
    /**
     * Rule 1 — AND RULE 3's PROTECTION, which this line used to be outside.
     *
     * An EMPTY list here means "the server has not indexed one yet", not
     * "this file has none". The distinction matters because `dated` maps
     * rather than filters, so `{records: []}` is truthy and overwrote
     * whatever the row held — including a record `redactionDataSaved` had
     * just patched in from a save that completed moments ago.
     *
     * That is the read-your-writes miss the save patch was written to avoid,
     * re-introduced one reducer over: save a file, navigate to the folder
     * view inside the indexing window (`refreshFileOutputs` fires for
     * exactly the file just closed), and the row reverts to "nobody has
     * worked on this" — the grey minus returns and the client goes back to
     * promising the combine an exclusion the server refuses.
     *
     * Unlike the outputs above, redaction data has no delete-to-zero path:
     * rotation always keeps the newest, and nothing in the UI removes the
     * last one. So an empty answer is always "not yet", never "gone".
     *
     * AND AN OLDER ANSWER IS THE SAME MISS. Guarding only the empty case
     * covered a file whose first save this is, and left the one class the
     * predicate exists for: a file carrying an UPLOAD STUB answers this
     * query (`limit: 1, orderBy createdDateTime desc`) with the stub while
     * the new save is still being indexed — non-empty, and older than what
     * the row already holds. Taking it put the never-worked-on minus back
     * on a file the reviewer had just saved, which is the whole defect.
     *
     * So: keep whichever is newer. A real newer record always wins, which
     * is what stops the patch going stale in the other direction.
     *
     * REFUSED ONLY WHEN IT CAN BE SHOWN TO BE OLDER, not whenever it cannot
     * be shown to be newer. `dated` normalises a record with no timestamp to
     * `''` rather than dropping it — the staleness pickers treat that as
     * "unknown", and an unknown date must not cost the row a record it would
     * otherwise have taken.
     */
    const freshRedactionData = dated(record.redactionDataAssets?.records);
    const incoming = freshRedactionData?.records[0]?.createdDateTime;
    const held = row.redactionDataAssets?.records?.[0]?.createdDateTime;
    const demonstrablyOlder = !!incoming && !!held && incoming < held;
    if (freshRedactionData?.records.length && !demonstrablyOlder) {
      row.redactionDataAssets = freshRedactionData;
    }
    row.redactionSelectionAssets =
      record.redactionSelectionAssets ?? row.redactionSelectionAssets;
  };

  // Rule 2: rebuild the flat list from the rows just patched, exactly as the
  // folder load does, so the two cannot disagree about what is downloadable.
  const patchFolderFiles = (caseFiles: CaseFilesType | null | undefined) => {
    if (!caseFiles?.inputFiles) {
      return;
    }
    caseFiles.inputFiles.forEach(patchRow);
    if (caseFiles.outputFiles) {
      caseFiles.outputFiles = mapOutputMediaFiles(caseFiles.inputFiles);
    }
  };
  state.selectedFolders.forEach((folder) => patchFolderFiles(folder.caseFiles));
  patchFolderFiles(state.selectedVirtualFolder.parent?.caseFiles);
  // And the retained request rows, which `redactedDocumentExported` patches
  // too (VE-30011) — a re-select inside the freshness window reuses them.
  Object.values(state.caseContentsRows ?? {}).forEach(patchFolderFiles);

  fresh.forEach((record) => {
    const unassigned = state.legacyTdoDetails[`id_${record.id}`];
    if (!unassigned) {
      return;
    }
    // Unassigned Media is DERIVED from this map by a selector that runs
    // mapOutputMediaFiles itself, so there is no flat list to rebuild here.
    unassigned.tasks = record.tasks ?? unassigned.tasks;
    // VE-28429: the same editor-changeable fields the folder rows get above.
    // Unassigned Media is never refetched on return from an editor, so this
    // refresh is the ONLY thing that can update it — without these two the
    // tile kept its pre-edit name for the rest of the session while the same
    // file inside a request showed the new one.
    if (record.name) {
      unassigned.name = record.name;
    }
    if (record.modifiedDateTime) {
      unassigned.modifiedDateTime = record.modifiedDateTime;
    }
    const unassignedHasOutputs =
      ((unassigned.redactedMediaAssets as { records?: unknown[] } | undefined)
        ?.records?.length ?? 0) > 0;
    if (hasOutputs(record) || !unassignedHasOutputs) {
      unassigned.redactedMediaAssets =
        record.redactedMediaAssets ?? unassigned.redactedMediaAssets;
      unassigned.auditLogAssets =
        record.auditLogAssets ?? unassigned.auditLogAssets;
      unassigned.redactedTranscriptAssets =
        record.redactedTranscriptAssets ?? unassigned.redactedTranscriptAssets;
    }
    unassigned.redactionDataAssets =
      record.redactionDataAssets ?? unassigned.redactionDataAssets;
    unassigned.redactionSelectionAssets =
      record.redactionSelectionAssets ?? unassigned.redactionSelectionAssets;
  });
};

type RedactedDocumentExportedPayload = ReturnType<
  typeof EXPORT_COMPLETED
>['payload'];

/**
 * A document burn-in just finished: record its output on the file's row at once
 * (VE-30011).
 *
 * The document editor's Documents panel reads its redacted/unredacted marker off
 * these rows (`selectEditorRequestDocuments`), and they were warmed once on the
 * way in. Nothing on the editor route refreshes them — `watchRefreshFileOutputs`
 * belongs to `caseMediaSaga`, which the editors cancel — so a file redacted for
 * the first time with the editor open kept its "not redacted yet" marker until
 * the reviewer left and came back.
 *
 * The burn-in saga ALSO asks for the server's answer (`fetchFileOutputs`), which
 * lands through `refreshFileOutputsSuccess` above. This patch is what makes the
 * marker correct without waiting for it, and what keeps it correct when that
 * read misses the asset just created: Rule 3 there treats an empty result as no
 * answer, which leaves a first-ever output unrecorded.
 *
 * REPLACES the row's outputs rather than prepending, matching the burn-in, which
 * deletes the older redacted-document assets once the new one is stored. The
 * row's `auditLogAssets` is replaced too when this burn-in created one, since
 * the previous CSV was deleted with it. Every copy of the row is patched — see
 * `forEachRowStore` — including the retained `caseContentsRows`, or
 * re-selecting the request inside its freshness window brings the stale copy
 * back. `refreshFileOutputsSuccess` reaches the same stores; keep them in step.
 */
export const redactedDocumentExported = (
  state: CaseMediaState,
  { payload }: { payload: RedactedDocumentExportedPayload }
) => {
  /**
   * The record as the casemedia query returns it: server timestamp (the
   * staleness check compares it with server-stamped redaction data) and the
   * asset's own `details`.
   *
   * COPIED WHOLE, not field by field. This used to list the fields it knew
   * about — `{ version }` — and the list fell behind the asset twice in one
   * change set: `burnInRequested` first, then `includedUnreviewed`, each
   * time leaving the row asserting something the server did not and costing
   * a release. Nothing connects adding a field in `exportPush` to adding it
   * here, so the only version of this that stays correct is the one that
   * does not choose. See the action's own note.
   */
  const output = {
    id: payload.versionId,
    name: payload.name,
    contentType: 'application/pdf',
    assetType: 'redacted-document',
    signedUri: payload.redactedAssetUrl ?? undefined,
    createdDateTime: payload.createdDateTime,
    details: payload.details,
    parentTdoId: payload.tdoId,
  } as unknown as AssetFileType;
  // The burn-in has already deleted the previous audit CSV, so a row left
  // pointing at it would download nothing.
  const auditLog = payload.auditLogAsset && {
    records: [
      {
        id: payload.auditLogAsset.id,
        name: payload.auditLogAsset.name,
        signedUri: payload.auditLogAsset.signedUri,
        assetType: 'redact-audit-log',
        createdDateTime: payload.createdDateTime,
        parentTdoId: payload.tdoId,
      },
    ] as unknown as AssetFileType[],
  };
  const patchRow = (row: AssetFileType | UnassignedTdoRecord) => {
    row.redactedMediaAssets = { records: [output] };
    if (auditLog) {
      row.auditLogAssets = auditLog;
    }
  };
  forEachRowStore(state, payload.tdoId, patchRow);
  // AND THE ROSTER, which `forEachRowStore` does not reach — without this a
  // document the folder query never paged in stays "nobody has worked on
  // this" in the Documents panel for the rest of the session, having just
  // been redacted. See the helper.
  patchRosterHasRedactedOutput(state, String(payload.tdoId), true);
};

type RedactedDocumentDeletedPayload = ReturnType<
  typeof DELETE_REDACTED_DOCUMENT_SUCCESS
>['payload'];

/**
 * A redacted document was deleted: take it off the file's rows (VE-30011).
 *
 * The inverse of `redactedDocumentExported`, and needed for the same reason —
 * nothing on the editor route refreshes these rows, so the Documents panel
 * marker stayed black (and the Redacted Files row stayed undimmed) for a file
 * whose only output had just been deleted. A refresh would not fix it either:
 * Rule 3 in `refreshFileOutputsSuccess` reads "no outputs" as "no answer" and
 * keeps what the row already has, which is exactly right for a read-your-writes
 * miss and exactly wrong for a delete. So the delete has to say so itself.
 *
 * The linked audit CSV goes with it when the last output is removed, because
 * the delete saga deletes that CSV too. Only `redact-audit-log` records are
 * dropped: a `blur-audit-log` belongs to the video flow, which has nothing to
 * do with this asset. That cleanup is best-effort server-side, so a row that
 * keeps its CSV after a failed cleanup is the cosmetic case, not a dangling
 * download.
 */
export const redactedDocumentDeleted = (
  state: CaseMediaState,
  { payload }: { payload: RedactedDocumentDeletedPayload }
) => {
  /**
   * Did a LOADED row end up with no outputs left?
   *
   * `null` means no loaded row carried this version at all, which on a
   * roster-only file is the ordinary case — and is the one state where the
   * roster must be left alone below: the boolean says an output exists, and
   * nothing here can tell whether the deleted one was the last.
   */
  let loadedRowLeftEmpty: boolean | null = null;
  const patchRow = (row: AssetFileType | UnassignedTdoRecord) => {
    const records = (
      row.redactedMediaAssets as { records?: AssetFileType[] } | undefined
    )?.records;
    if (!records?.some((record) => record.id === payload.versionId)) {
      return;
    }
    const remaining = records.filter(
      (record) => record.id !== payload.versionId
    );
    row.redactedMediaAssets = { records: remaining };
    loadedRowLeftEmpty = remaining.length === 0;
    if (remaining.length > 0) {
      return;
    }
    const auditRecords = (
      row.auditLogAssets as { records?: AssetFileType[] } | undefined
    )?.records;
    if (auditRecords?.length) {
      row.auditLogAssets = {
        records: auditRecords.filter(
          (record) => record.assetType !== 'redact-audit-log'
        ),
      };
    }
  };
  forEachRowStore(state, payload.tdoId, patchRow);
  // THE MIRROR OF THE EXPORT PATCH, and deliberately not its equal. The
  // export knows an output exists; a delete only knows ONE went, so the
  // roster's boolean is cleared solely where a loaded row proves it was the
  // last. With no loaded row there is nothing to prove it either way, and
  // "there is an output" is the safer of the two to be wrong about: it
  // leaves the file alone rather than offering it up as unworked.
  if (loadedRowLeftEmpty) {
    patchRosterHasRedactedOutput(state, String(payload.tdoId), false);
  }
};

/**
 * A REVIEWER SAVED REDACTIONS: put the record on the file's rows.
 *
 * The third of these patches, for the third version of one problem — nothing
 * on the editor route refreshes these rows, so what the editor writes, the
 * rows do not hear. `redactedDocumentExported` says so for an output,
 * `redactedDocumentDeleted` for a deleted one; this one says it for the save
 * that precedes both.
 *
 * WHAT IT FIXES. `hasReviewerSavedRedactions` — the predicate behind the row's
 * dashed "needs redacting" ring, the grey never-worked-on minus, the set a
 * merged PDF is allowed to leave OUT, and the list Redact All burns — reads
 * the newest `document-redaction-data` record off these rows. Save a file for
 * the first time and the rows still said nobody had touched it: the badge
 * cleared, the minus stayed, no ring appeared, and the client went on
 * promising the combine an exclusion the server (which reads the assets live)
 * refuses to make, so Generate failed with "1 file has not been redacted yet".
 *
 * NOT A REFETCH. Autosave fires every few minutes and on every manual save, so
 * a network round trip here would be constant traffic for a value we already
 * hold. The patch is free and exact.
 *
 * It is also no longer undone by one. This claimed that
 * `refreshFileOutputsSuccess` Rule 3 would swallow a read-your-writes miss —
 * it would not: Rule 3 guarded the OUTPUT lists and the redaction-data line
 * sat outside it, overwriting this patch with an empty server answer. The
 * guard it assumed is now actually there; see Rule 1 over in that reducer.
 *
 * NOT A SESSION OVERRIDE EITHER, which was the first attempt: a selector over
 * the editor's live and suspended sessions. It could not survive its own use
 * case. `SAVE_SUCCESS` leaves a session `clean`, and `suspendIfWorthKeeping`
 * parks only dirty or mid-save ones — so saving a file and switching away
 * DISCARDED the evidence, and the row reverted to "nobody has worked on this"
 * in exactly the "work through several files, then redact all" flow the fix
 * was for. A row patch outlives the session, and corrects every reader rather
 * than the three that were remembered to be threaded.
 *
 * `writtenBy` IS CARRIED, and absent for an ordinary save. That marker is what
 * distinguishes a save a PERSON made from one a pipeline wrote — see
 * `isMachineWrittenRedactionAsset` — and both kinds reach this reducer. It was
 * hardcoded absent on the reasoning that only people save; the burn of a file
 * released without review also saves (VE-32440), and patching that one in
 * unmarked told every reader a reviewer had worked on a file nobody had
 * opened, which is the one claim this whole mechanism exists to get right.
 *
 * REPLACES rather than prepends: the row carries `limit: 1` worth of history
 * (the newest record), and this save is now the newest. The server's copy
 * arrives on the next real fetch.
 */
export const redactionDataSaved = (
  state: CaseMediaState,
  { payload }: { payload: ReturnType<typeof SAVE_SUCCESS>['payload'] }
) => {
  const record = {
    // The real asset where the caller has it. Synthesized otherwise: no reader
    // of these records matches on the id, and a record with the right
    // `details` and date is worth more than no record at all.
    id: payload.assetId ?? `local-save-${payload.tdoId}-${payload.savedAt}`,
    createdDateTime: payload.savedAt,
    details: {
      /**
       * BOTH HALVES OF THE VE-26422 LABEL, or neither works.
       *
       * `readBurnInLabel` tests `burnInRequested` FIRST and returns
       * `unlabelled` when it is not a boolean — so a record carrying only
       * `expectedRedactedVersion` can never reach `saved` or `burnedIn`, and
       * every patched row silently fell back to the pre-VE-26422 timestamp
       * comparison. That fallback has a known hole this label exists to
       * close: an edit saved WHILE a burn runs lands before the output and
       * reads as included, so the row reported current while the reviewer's
       * last redaction was missing from the released file.
       *
       * `services.ts` writes the key on every save including as `false`,
       * because three states have to stay distinguishable (sent for burn-in
       * / saved and not sent / written before labelling existed). The patch
       * has to write it the same way or it is a fourth.
       */
      burnInRequested: payload.expectedRedactedVersion !== undefined,
      ...(payload.expectedRedactedVersion === undefined
        ? {}
        : { expectedRedactedVersion: payload.expectedRedactedVersion }),
      // Absent for a person's save, which is what makes an absent marker
      // mean "a human saved this" — see the header.
      ...(payload.writtenBy ? { writtenBy: payload.writtenBy } : {}),
    },
  };
  forEachRowStore(state, String(payload.tdoId), (row) => {
    row.redactionDataAssets = { records: [record] };
  });
};

/**
 * Apply a document-editor rename to the file's rows (VE-31012); nothing on the
 * editor route refreshes them. Runs on SUCCESS, so a failure has nothing to undo.
 *
 * Writes `name` — the list view and the Documents panel read it — and, when the
 * row carries `details`, the same name copies the rename writes to the TDO:
 * `details.name` and `details.veritoneFile.{filename,fileName}`, the pair the
 * thumbnail tile reads ahead of `name`. No row reader uses `details.name`, but
 * the folder view's status change and soft delete spread the row's whole
 * `details` back into `updateTDO` (sagas ~2425 and ~1857), so a copy left stale
 * here is a copy written back over the rename.
 *
 * `modifiedDateTime` goes with them when the mutation reported one — the
 * folder view shows that date in its details column and sorts on it, so a row
 * reused after a short editor visit would otherwise pair the new name with the
 * pre-rename date, in the pre-rename position.
 *
 * `details` is never invented where the row has none: the tile falls back to
 * `name`, and a reload brings the server's copy in.
 */
export const documentRenamed = (
  state: CaseMediaState,
  {
    payload,
  }: { payload: ReturnType<typeof RENAME_DOCUMENT_SUCCESS>['payload'] }
) => {
  forEachRowStore(state, payload.tdoId, (row) => {
    row.name = payload.name;
    if (payload.modifiedDateTime) {
      row.modifiedDateTime = payload.modifiedDateTime;
    }
    const details = row.details as
      | {
          name?: string;
          veritoneFile?: { filename?: string; fileName?: string } | null;
        }
      | undefined;
    if (!details) {
      return;
    }
    details.name = payload.name;
    if (details.veritoneFile) {
      details.veritoneFile.filename = payload.name;
      details.veritoneFile.fileName = payload.name;
    }
  });
};

/**
 * Every place casemedia holds a file's row, so the writers that patch outputs
 * in place cannot disagree about which copies they reach (VE-30011): the
 * selected folders, the virtual folder's parent, the retained request rows
 * (`caseContentsRows`, reused on a quick re-select), and the Unassigned Media
 * record. Each folder copy has its flat `outputFiles` list rebuilt after the
 * patch, as a folder load does.
 */
const forEachRowStore = (
  state: CaseMediaState,
  tdoId: string,
  patch: (row: AssetFileType | UnassignedTdoRecord) => void
) => {
  const patchFolderFiles = (caseFiles: CaseFilesType | null | undefined) => {
    const inputFiles = caseFiles?.inputFiles;
    const row = inputFiles?.find((file) => file.id === tdoId);
    if (!caseFiles || !inputFiles || !row) {
      return;
    }
    patch(row);
    if (caseFiles.outputFiles) {
      caseFiles.outputFiles = mapOutputMediaFiles(inputFiles);
    }
  };
  state.selectedFolders.forEach((folder) => patchFolderFiles(folder.caseFiles));
  patchFolderFiles(state.selectedVirtualFolder.parent?.caseFiles);
  Object.values(state.caseContentsRows ?? {}).forEach(patchFolderFiles);
  const unassigned = state.legacyTdoDetails[`id_${tdoId}`];
  if (unassigned) {
    patch(unassigned);
  }
};

/**
 * THE ROSTER HEARS ABOUT IT TOO (VE-32420).
 *
 * `forEachRowStore` above reaches every copy of a LOADED row — the selected
 * folders, the virtual folder, the retained request rows, the unassigned
 * details. It does not reach `requestRoster`, which is not a row store: it is
 * the request's complete file list, and for any document the heavy per-row
 * query has not paged in it is the ONLY thing the editor has.
 *
 * A roster entry cannot hold an asset record — see `fileHasRedactedOutput`
 * for why it must not fake one — so what it carries is the boolean, and the
 * boolean is as capable of going stale as the list is. Burn such a file and
 * nothing told the roster; `hasRedactedOutput` kept the value it was fetched
 * with, and the Documents panel went on calling a file it had just redacted
 * one nobody had ever worked on, for the rest of the session. The editor
 * never window-fetches, so there was no later read to correct it.
 *
 * Every roster is scanned rather than one looked up by case, the way
 * `pruneRosterEntriesInPlace` does: these patches are handed a tdo and the
 * request it belongs to is not on the action.
 */
const patchRosterHasRedactedOutput = (
  state: CaseMediaState,
  tdoId: string,
  hasRedactedOutput: boolean
) => {
  for (const roster of Object.values(state.requestRoster ?? {})) {
    const entry = roster?.entries?.find(
      (candidate) => String(candidate.id) === tdoId
    );
    if (entry) {
      entry.hasRedactedOutput = hasRedactedOutput;
    }
  }
};

export const setLastActiveFolderViewFile = (
  state: CaseMediaState,
  {
    payload: { caseId, tdoId, isReturnScrollPending },
  }: {
    payload: ActionSetLastActiveFolderViewFilePayload;
  }
) => {
  // Carry over the pending flag from the previous entry only when it's for the
  // same case (a case switch is handled separately and resets this state).
  const prev = state.lastActiveFolderViewFile;
  const samePrev = prev && prev.caseId === caseId ? prev : null;
  return {
    ...state,
    lastActiveFolderViewFile: {
      caseId,
      tdoId,
      // A real open requests a return-scroll; an in-place action must not cancel
      // one that's still pending (e.g. if the user touches a row before the
      // post-return scroll has fired while the list is still loading).
      isReturnScrollPending:
        isReturnScrollPending || !!samePrev?.isReturnScrollPending,
    },
    // Only an actual open (navigation) records the per-case "last opened" file
    // that drives the badge + return scroll. In-place actions (kebab / status /
    // download / review) just move the highlight above. This map is NOT reset on
    // case switch, so the badge survives leaving the case and coming back.
    ...(isReturnScrollPending && {
      lastOpenedFolderViewFileByCase: {
        ...state.lastOpenedFolderViewFileByCase,
        [caseId]: tdoId,
      },
    }),
  };
};

/**
 * Patch a folder row's status to `recorded` after the PDF fast path
 * wrote it server-side — the field `FileTile` / `FolderItem` gate on. No-op when
 * the file is not in the folder on screen; the fetch that brings it in carries
 * the real status.
 */
export const markTdoRecorded = (
  state: CaseMediaState,
  { payload: { tdoId } }: { payload: { tdoId: string } }
) => {
  const files = state.selectedVirtualFolder.parent?.caseFiles?.inputFiles;
  const idx = files?.findIndex((f) => f.id === tdoId) ?? -1;
  const row = idx >= 0 ? files?.[idx] : undefined;
  if (files && row && row.status !== 'recorded') {
    files[idx] = { ...row, status: 'recorded' };
  }
};

export const markFolderViewTdoOpened = (
  state: CaseMediaState,
  {
    payload: { tdoId },
  }: {
    payload: { tdoId: TDOId };
  }
) => ({
  ...state,
  openedFolderViewTdoIds: state.openedFolderViewTdoIds.includes(tdoId)
    ? state.openedFolderViewTdoIds
    : [...state.openedFolderViewTdoIds, tdoId],
});

export const consumeFolderViewReturnScroll = (state: CaseMediaState) => ({
  ...state,
  lastActiveFolderViewFile: state.lastActiveFolderViewFile
    ? {
        ...state.lastActiveFolderViewFile,
        isReturnScrollPending: false,
      }
    : null,
});

export const clearLastActiveFolderViewFile = (state: CaseMediaState) => ({
  ...state,
  lastActiveFolderViewFile: null,
});

export const clearLastOpenedFolderViewFileForCase = (
  state: CaseMediaState,
  {
    payload: { caseId, tdoId },
  }: {
    payload: { caseId: CaseId; tdoId?: TDOId };
  }
) => {
  const currentTdoId = state.lastOpenedFolderViewFileByCase[caseId];
  if (!currentTdoId || (tdoId && currentTdoId !== tdoId)) {
    return state;
  }

  const { [caseId]: _removed, ...rest } = state.lastOpenedFolderViewFileByCase;
  return {
    ...state,
    lastOpenedFolderViewFileByCase: rest,
  };
};

const mapFilesFetchStatus = (
  state: CaseMediaState,
  isCase: boolean,
  isProgress: boolean,
  selectedId: FolderId
) => {
  if (isCase) {
    const virtualFolderName = state?.selectedVirtualFolder?.virtualFolderName;
    if (virtualFolderName) {
      return {
        ...state.selectedVirtualFolder,
        fetchingFiles: isProgress,
      };
    } else {
      const checkExists = state.selectedFolders.filter((f) =>
        [f.treeObjectId, f.id].includes(selectedId)
      );
      if (checkExists?.length > 0) {
        const defaultVirtual: VirtualFolderStateType = {
          virtualFolderName: VirtualFolderNames.INPUT,
          // The full selected entry, not a minimal `{id}` placeholder: this
          // parent is re-dispatched by the media editor's exit re-select, and
          // a folderPath-less object there collapses the whole
          // selected-folders chain into one unrenderable entry (VE-25873).
          parent: { ...checkExists[0] } as VirtualFolderStateType['parent'],
          fetchingFiles: isProgress,
          deletingFileInProgress: false,
        };
        return defaultVirtual;
      }
    }
  }
  return { ...state.selectedVirtualFolder };
};

const mapSelectedWithSubFolders = (
  selectedId: string,
  selectedFolders: FolderStateType[],
  action: Action<
    { folder: FolderStateType },
    { variables: { limit: number; offset: number } }
  >,
  isCase = false
) =>
  (selectedFolders || []).map((folder) => {
    if (folder?.id === selectedId || folder?.treeObjectId === selectedId) {
      const { limit, offset } = action.meta.variables;
      folder.fetchingSubFolders = false;
      folder.fetchSubFolderLimit = limit;
      folder.fetchSubFolderOffset = offset;
      const { childFolders, childTDOs } = action.payload.folder;
      if (isCase) {
        folder.fetchMoreAvailable = childTDOs?.records?.length === limit;
        folder.caseFiles = mapMediaFiles(childTDOs?.records || []);
        folder.exportedTdo = getExportedTdo(action.payload?.folder);
        // the request's templates arrive with its contents, which is
        // what makes project discovery free. Kept when the response omits them
        // rather than cleared — the folder may already hold templates loaded
        // by the tree fetch, and an absent field here means "not selected",
        // not "none".
        const { contentTemplates } = action.payload.folder;
        if (contentTemplates) {
          folder.contentTemplates = contentTemplates;
        }
      } else {
        folder.fetchMoreAvailable =
          (childFolders?.records?.length || 0) === limit;
        folder.subfolders = childFolders?.records || [];
      }
    }
    return folder;
  });

const getExportedTdo = (folder: FolderStateType) => {
  const exportedTdo = (folder?.childTDOs?.records || []).filter(
    (tdo) => tdo?.details?.isExport === true
  )?.[0];
  if (exportedTdo) {
    return {
      ...exportedTdo,
      downloadAllStarted: false,
      parentFolderId: folder?.treeObjectId,
    };
  }
  return null;
};

/**
 * record a request's project pointers in folder state once the
 * server has them.
 */
const recordProjectPointers = (
  state: CaseMediaState,
  action: ReturnType<
    typeof CREATE_PROJECT_SUCCESS | typeof DELETE_PROJECT_SUCCESS
  >
) => {
  const { caseId, pointers } = action.payload;
  (state.selectedFolders || []).forEach((folder: FolderStateType) => {
    // Matched on EITHER id: a column's folder is keyed by `id` under v2
    // folders and by `treeObjectId` under v1, and the action carries whichever
    // the clicking column had (VE-21114).
    const matches =
      (folder?.id as string | undefined) === (caseId as string) ||
      (folder?.treeObjectId as string | undefined) === (caseId as string);
    if (!matches) {
      return;
    }
    const target =
      pickProjectsContentTemplate(folder.contentTemplates) ??
      pickCaseContentTemplate(folder.contentTemplates);
    if (!target?.sdo?.data) {
      return;
    }
    target.sdo.data.projects = pointers as ProjectPointer[];
  });
};

const mapMediaFiles = (mediaFiles?: AssetFileType[]): CaseFilesType | null => {
  if (mediaFiles && mediaFiles.length > 0) {
    // Membership is `isRequestInputFile`, shared with the roster read so the
    // rows on screen and the index behind them cannot disagree about what is
    // in the request — see `requestMembership`.

    // Show every TDO in the consolidated folder column, including ones that
    // are still ingesting or whose MIME hasn't been resolved yet. Export-only
    // TDOs are surfaced separately via `getExportedTdo` so we exclude them
    // here. The row-level dot indicator in FolderItem communicates pending /
    // error state, and `primaryAsset.contentType` populates the proper icon
    // / type once ingest completes. fileType-based rules (future) will read
    // `veritoneFile.fileType` and `primaryAsset.contentType` directly off the
    // row at that point.
    //
    // project TDOs are excluded on the same principle. A project
    // holds a request's COMBINED redacted output; it is not a source document
    // and must never present as one. This is the only place the exclusion is
    // written because `caseFiles.inputFiles` is the single source for the
    // folder column, the editor's Documents panel
    // (`selectEditorRequestDocuments`), the media drawer and the grid — but it
    // is NOT the only place a project TDO can surface. Anything reading
    // `childTDOs` directly rather than going through here (search, trash,
    // Download All, the media-list export) needs its own guard.
    const filteredMedia: AssetFileType[] =
      mediaFiles.filter(isRequestInputFile);

    // Previous kind-discrimination filter — kept for reference. Used to gate
    // visibility on a MIME match (media + documents) or an external-system
    // request ID. Restore / adapt when implementing the future rule-based
    // "only allow specific filetypes to be processed" feature. Also re-add
    // `import { DOCUMENT_REDACTION_MIME_TYPES } from '@helpers/constants'`
    // when uncommenting.
    //
    // const filteredMedia: AssetFileType[] = filterOutSoftDeleted?.filter((i) => {
    //   // VE-21971: check BOTH veritoneFile.fileType and primaryAsset.contentType
    //   // instead of falling back via `||`. A truthy-but-non-MIME placeholder in
    //   // veritoneFile.fileType (e.g. "unknown") used to mask the real
    //   // primaryAsset.contentType and silently drop the file from the folder.
    //   const candidateTypes = [
    //     (i.details || i)?.veritoneFile?.fileType,
    //     i?.primaryAsset?.contentType,
    //   ].filter((t): t is string => !!t);
    //   const isExternalId = !(
    //     isNil(i.details?.govQARequestId) &&
    //     isNil(i.details?.foiaXpressRequestId) &&
    //     isNil(i.details?.casepointRequestId) &&
    //     isNil(i.details?.nuixRequestId) &&
    //     isNil(i.details?.exterroRequestId)
    //   );
    //   // IMAGE-TODO DOC-TODO need to better centralize this
    //   const isDocument = candidateTypes.some((t) =>
    //     (DOCUMENT_REDACTION_MIME_TYPES as readonly string[]).some((mimeType) =>
    //       t.includes(mimeType)
    //     )
    //   );
    //   const isMedia = candidateTypes.some(
    //     (t) => t.includes('video') || t.includes('audio') || t.includes('image')
    //   );
    //   if (isExternalId || isDocument || isMedia) {
    //     return i;
    //   }
    // });

    const inputFiles = filteredMedia.map((file) => ({
      ...file,
      folders: file.folders?.map((f) => ({
        ...f,
        folderPath: sortFolderPath(f.folderPath || []),
      })),
    }));
    const outputFiles = [...mapOutputMediaFiles(filteredMedia)];
    return {
      inputFiles,
      outputFiles,
    };
  }
  return null;
};

/**
 * Flatten each file's output assets into the flat list the download paths
 * expect. Exported because VE-26943's synthetic Unassigned Media folder has to
 * build the same list from the same fields — its files come from the SDO
 * enrichment rather than fetchCaseContentsQuery, but the asset records are
 * identical, and a second copy of this would drift.
 */
export const mapOutputMediaFiles = (
  mediaFiles: AssetFileType[]
): AssetFileType[] => {
  const outputFiles: AssetFileType[] = [];
  mediaFiles.forEach((file) => {
    // VE-26943: stamp the parent as we flatten — this is the one moment the
    // link exists, and every consumer downstream had been re-deriving it from
    // the asset id's shape.
    const owned = (records?: AssetFileType[]) =>
      (records || []).map((record) => ({ ...record, parentTdoId: file.id }));
    outputFiles.push(...owned(file.redactedMediaAssets?.records));
    outputFiles.push(...owned(file.auditLogAssets?.records));
    outputFiles.push(...owned(file.redactedTranscriptAssets?.records));
  });
  return outputFiles;
};

const fetchSubFoldersByIdFailure = (
  state: CaseMediaState,
  action: { payload: { message: string }[] }
) => ({
  ...state,
  fetchingRootFolders: false,
  selectedVirtualFolder: {
    ...state.selectedVirtualFolder,
    fetchingFiles: false,
  },
  selectedFolders: state.selectedFolders.map((folder) => ({
    ...folder,
    fetchingSubFolders: false,
  })),
  fetchingSubFoldersError: get(
    action,
    'payload[0].message',
    'Error fetching sub folders'
  ),
});

const setSelectedVirtualFolderRequest = (
  state: CaseMediaState,
  action: {
    payload: {
      folder: FolderStateType<CaseId>;
      virtualFolderName: VirtualFolderNames | undefined;
    };
  }
): CaseMediaState => {
  const { folder, virtualFolderName } = action.payload;
  // VE-25098: opening a different case's files drops a selection made in the
  // previous case (multi-select is scoped to one case).
  const multiSelectReset =
    state.multiSelectCaseId && state.multiSelectCaseId !== folder?.id
      ? {
          multiSelectedFiles: [],
          multiSelectCaseId: null,
          multiSelectAnchorId: null,
          pendingRemovalFileIds: [],
        }
      : {};
  const updatedState: CaseMediaState = {
    ...state,
    ...multiSelectReset,
    selectedVirtualFolder: {
      ...state.selectedVirtualFolder,
      parent: folder,
      virtualFolderName: virtualFolderName,
      fetchingFiles: false,
    },
  };
  return updatedState;
};

const deleteFolderInit = (
  state: CaseMediaState,
  action: Action<undefined, { variables: { id: TreeObjectId } }>
): CaseMediaState => {
  const updatedState: CaseMediaState = {
    ...state,
    selectedFolders: mapDeletetingFolderStatus(
      state.selectedFolders,
      action?.meta.variables.id,
      true
    ),
  };
  return updatedState;
};
const mapDeletetingFolderStatus = (
  selectedFolders: FolderStateType[],
  treeObjectId: TreeObjectId,
  inProgress: boolean
) =>
  cloneDeep([...(selectedFolders || [])]).map((folder: FolderStateType) => {
    if (folder.subfolders) {
      folder.subfolders.forEach((sub) => {
        if (sub.treeObjectId === treeObjectId) {
          sub.deletingFolderInProgress = inProgress;
        }
      });
    }
    return folder;
  });

const deleteFolderSuccess = (
  state: CaseMediaState,
  action: Action<any, { variables: { id: TreeObjectId } }>
): CaseMediaState => {
  const { id } = action.meta.variables;
  const updatedSelectedFolders = [
    ...mapDeletedFolders(state.selectedFolders, id),
  ];
  const updatedVirtualFolders = {
    ...mapDeletedVirtualFolder(
      state.selectedVirtualFolder,
      updatedSelectedFolders
    ),
  };
  const updatedState: CaseMediaState = {
    ...state,
    selectedFolders: updatedSelectedFolders,
    selectedVirtualFolder: updatedVirtualFolders,
  };
  return updatedState;
};

const softDeleteFolderSuccess = (
  state: CaseMediaState,
  action: {
    payload: {
      id: TreeObjectId;
      folderName: string;
    };
  }
): CaseMediaState => {
  const { id } = action.payload;
  const updatedSelectedFolders = [
    ...mapDeletedFolders(state.selectedFolders, id),
  ];
  const updatedVirtualFolders = {
    ...mapDeletedVirtualFolder(
      state.selectedVirtualFolder,
      updatedSelectedFolders
    ),
  };
  // VE-26484: root-level folders live in rootFolder.subfolders, not
  // selectedFolders, so mapDeletedFolders alone leaves them in the tree until
  // a refetch. Prune both caches (mirroring renameFolderSuccess) so the folder
  // disappears immediately. Match id too: v2 folder nodes are keyed by folder
  // id (see deleteCaseSdoRequest's outcome predicate for the same dual match).
  const updatedRootFolder: FolderStateType = {
    ...state.rootFolder,
    subfolders: (state.rootFolder?.subfolders || []).filter(
      (sub: FolderStateType) =>
        sub.treeObjectId !== id && String(sub.id) !== String(id)
    ),
  };
  // VE-26484: the prune above only cleans the cache we hold right now. The move
  // into Trash is still in flight, so until it commits the server keeps handing
  // the folder back under its old parent — latch the id so mapRootFolder filters
  // it out of any refetch that lands meanwhile (the trash saga releases it once
  // the move settles).
  const pendingTrashFolderIds = state.pendingTrashFolderIds || [];
  const updatedState: CaseMediaState = {
    ...state,
    rootFolder: updatedRootFolder,
    pendingTrashFolderIds: pendingTrashFolderIds.includes(id)
      ? pendingTrashFolderIds
      : [...pendingTrashFolderIds, id],
    selectedFolders: updatedSelectedFolders,
    selectedVirtualFolder: updatedVirtualFolders,
  };
  return updatedState;
};

// VE-26484: the trash move has settled (committed, failed, or the saga's wait
// timed out) — stop hiding these folders so the next refetch reflects whatever
// the server actually says.
const releasePendingTrashFolders = (
  state: CaseMediaState,
  { payload }: { payload: TreeObjectId[] }
) => {
  const releasing = new Set<string>(payload.map(String));
  state.pendingTrashFolderIds = (state.pendingTrashFolderIds || []).filter(
    (id) => !releasing.has(String(id))
  );
};

const mapDeletedFolders = (
  selectedFolders: FolderStateType[],
  treeObjectId: TreeObjectId
) => {
  let filteredFolders: FolderStateType[] = [];
  const selectedIndex = selectedFolders.findIndex(
    (folder: FolderStateType) => folder.treeObjectId === treeObjectId
  );
  if (selectedIndex !== -1) {
    filteredFolders = selectedFolders.filter(
      (_folder: FolderStateType, index: number) => index < selectedIndex
    );
  } else {
    filteredFolders = [...selectedFolders];
  }
  return cloneDeep(filteredFolders).filter((folder: FolderStateType) => {
    if (folder.subfolders) {
      folder.subfolders = [
        ...folder.subfolders.filter(
          (sub: FolderStateType) => sub.treeObjectId !== treeObjectId
        ),
      ];
    }
    return folder;
  });
};
const mapDeletedVirtualFolder = (
  selectedVirtualFolders: VirtualFolderStateType,
  selectedFolders: FolderStateType[]
) => {
  const filtered = (selectedFolders || []).filter(
    (folder: FolderStateType) =>
      folder.id === selectedVirtualFolders?.parent?.id
  );
  return filtered?.length > 0 ? { ...selectedVirtualFolders } : {};
};

const deleteFolderFailure = (
  state: CaseMediaState,
  action: Action<
    ReadonlyArray<{ message?: string }> | undefined,
    { variables: { id: TreeObjectId } }
  >
) => {
  state.selectedFolders = mapDeletetingFolderStatus(
    state.selectedFolders,
    action.meta.variables.id,
    false
  );
  return;
};

const softDeleteFolderFailure = (
  state: CaseMediaState,
  action: {
    payload: { id: TreeObjectId; folderName: string };
  }
) => {
  state.selectedFolders = mapDeletetingFolderStatus(
    state.selectedFolders,
    action.payload.id,
    false
  );
  return;
};

const deleteFileInit = (state: CaseMediaState) => {
  state.selectedVirtualFolder.deletingFileInProgress = true;
  return;
};

/**
 * Drop files from the request roster once they are gone (VE-32420).
 *
 * The list renders one row per ROSTER entry — loaded rows only enrich them
 * (`mergeRosterWithLoaded`). So removing a row from `caseFiles` is not enough
 * to make it leave the screen: the next render rebuilds it from the roster
 * entry nobody touched, now stripped back to a roster-only row. Trashing a
 * file made it animate out and come straight back, spinner and all, until the
 * request was reopened.
 *
 * Every roster in the map is swept rather than one keyed by case: the map holds
 * the open request by construction, and a delete arriving for anything else is
 * a file that should not be in a roster either way.
 */
const pruneRosterEntriesInPlace = (
  state: CaseMediaState,
  tdoIds: readonly TDOId[]
) => {
  const removed = new Set(tdoIds.map((id) => String(id)));
  if (!removed.size) {
    return;
  }
  for (const [caseId, roster] of Object.entries(state.requestRoster ?? {})) {
    const entries = roster?.entries;
    if (!entries?.length) {
      continue;
    }
    const kept = entries.filter((entry) => !removed.has(String(entry.id)));
    if (kept.length !== entries.length) {
      state.requestRoster[caseId] = { ...roster, entries: kept };
    }
  }
};

const deleteFileSuccess = (
  state: CaseMediaState,
  action: Action<any, { variables: ActionDeleteFilePayload }>
) => {
  const { tdoId, type, isLegacyTdo } = action.meta.variables;
  state.selectedFolders = mapDeleteFileSuccess(state, tdoId, type);
  state.selectedVirtualFolder = mapDeleteVirtualFolderFile(state, tdoId, type);
  // The OUTPUT kind removes a file's redacted output, not the file — its input
  // row stays on screen, so its roster entry has to stay too.
  if (type !== VirtualFolderNames.OUTPUT) {
    pruneRosterEntriesInPlace(state, [tdoId]);
  }
  // VE-26943: an unassigned file lives in LegacyMedias, not in a folder's
  // caseFiles, so the maps above can't remove it. Drop it here instead of
  // refetching the list — a refetch starts at offset 0 and would throw away
  // every page the user had scrolled in.
  if (isLegacyTdo) {
    removeUnassignedFileInPlace(state, (m) => m.tdoId === tdoId);
  }
  return;
};

const mapDeletedCaseFiles = (
  caseFiles: CaseFilesType,
  tdoId: TDOId,
  type?: VirtualFolderNames
): CaseFilesType => {
  const filteredInputFiles = caseFiles?.inputFiles?.reduce(
    (acc: AssetFileType[], file) => {
      if (file.id === tdoId) {
        if (type === VirtualFolderNames.OUTPUT) {
          acc.push({
            ...file,
            redactedMediaAssets: {
              records: [],
            },
            auditLogAssets: {
              records: [],
            },
          });
        }
        return acc;
      }
      acc.push(file);
      return acc;
    },
    []
  );
  const filteredOutputFiles = caseFiles?.outputFiles?.filter(
    (file: AssetFileType) =>
      file.id !== tdoId && file.id?.split('_')?.[0] !== tdoId
  );
  const updatedCaseFiles: CaseFilesType = {
    inputFiles: [...(filteredInputFiles || [])],
    outputFiles: [...(filteredOutputFiles || [])],
  };
  return updatedCaseFiles;
};

const mapDeleteVirtualFolderFile = (
  state: CaseMediaState,
  tdoId: string,
  type?: VirtualFolderNames
): VirtualFolderStateType => {
  const caseFiles = {
    ...(state.selectedVirtualFolder?.parent?.caseFiles || {}),
  };
  if (!isEmpty(caseFiles)) {
    const parentFolder: FolderStateType<CaseId> = {
      ...get(state, 'selectedVirtualFolder.parent', {}),
      caseFiles: mapDeletedCaseFiles(caseFiles, tdoId, type),
    };
    const virtualFolder = {
      ...state.selectedVirtualFolder,
      parent: {
        ...parentFolder,
        id: parentFolder.id,
      },
    };
    return virtualFolder;
  }
  return state.selectedVirtualFolder;
};

const mapDeleteFileSuccess = (
  state: CaseMediaState,
  tdoId: string,
  type?: VirtualFolderNames
) => {
  const parentId = state?.selectedVirtualFolder?.parent?.id;
  if (parentId) {
    return state.selectedFolders.map((folder: FolderStateType) => {
      if (folder.id === parentId && folder.caseFiles) {
        folder.caseFiles = mapDeletedCaseFiles(folder.caseFiles, tdoId, type);
      }
      return folder;
    });
  }
  return state.selectedFolders;
};

const deleteFileFailure = (state: CaseMediaState): CaseMediaState => {
  const updatedState: CaseMediaState = {
    ...state,
    selectedVirtualFolder: {
      ...state.selectedVirtualFolder,
      deletingFileInProgress: false,
    },
  };
  return updatedState;
};

// VE-25098: multi-file selection ------------------------------------------

// Mutates the draft to reset the multi-select fields. Used by the explicit
// clear action and by folder-navigation handlers so a stale selection doesn't
// linger after the user leaves the case it belongs to.
const resetMultiSelect = (state: CaseMediaState) => {
  state.multiSelectedFiles = [];
  state.multiSelectCaseId = null;
  state.multiSelectAnchorId = null;
};

const toggleFileMultiSelect = (
  state: CaseMediaState,
  {
    payload,
  }: { payload: { file: { id: TDOId; name: string }; caseId: CaseId } }
) => {
  const { file, caseId } = payload;
  // Selection is scoped to one case — a toggle in a different case starts fresh.
  if (state.multiSelectCaseId !== caseId) {
    state.multiSelectedFiles = [{ id: file.id, name: file.name }];
    state.multiSelectCaseId = caseId;
    state.multiSelectAnchorId = file.id;
    return;
  }
  const exists = state.multiSelectedFiles.some((f) => f.id === file.id);
  state.multiSelectedFiles = exists
    ? state.multiSelectedFiles.filter((f) => f.id !== file.id)
    : [...state.multiSelectedFiles, { id: file.id, name: file.name }];
  // The clicked row becomes the anchor for a subsequent Shift+Click range.
  state.multiSelectAnchorId = file.id;
};

const selectFileMultiRange = (
  state: CaseMediaState,
  {
    payload,
  }: { payload: { files: { id: TDOId; name: string }[]; caseId: CaseId } }
) => {
  const { files, caseId } = payload;
  state.multiSelectedFiles = files.map((f) => ({ id: f.id, name: f.name }));
  state.multiSelectCaseId = caseId;
  // Keep the existing anchor so successive Shift+Clicks pivot from the same row.
  if (!state.multiSelectAnchorId) {
    state.multiSelectAnchorId = files[0]?.id ?? null;
  }
};

const clearFileMultiSelect = (state: CaseMediaState) => {
  resetMultiSelect(state);
};

const setPendingRemovalFiles = (
  state: CaseMediaState,
  { payload }: { payload: TDOId[] }
) => {
  state.pendingRemovalFileIds = payload;
};

const releasePendingRemovalFiles = (
  state: CaseMediaState,
  { payload }: { payload: TDOId[] }
) => {
  const releasing = new Set(payload);
  state.pendingRemovalFileIds = state.pendingRemovalFileIds.filter(
    (id) => !releasing.has(id)
  );
};

const clearPendingRemovalFiles = (state: CaseMediaState) => {
  state.pendingRemovalFileIds = [];
};

const setBatchOperation = (
  state: CaseMediaState,
  { payload }: { payload: BatchOperationState | null }
) => {
  state.batchOperation = payload;
};

// Drop the given files from the open case and virtual folder without a server
// refetch — used to finalize a successful batch move (VE-25098). Reuses
// mapDeletedCaseFiles (the same removal the single-file delete flow uses) per
// id so the two paths stay consistent.
const removeCaseInputFiles = (
  state: CaseMediaState,
  { payload }: { payload: TDOId[] }
) => {
  const applyRemovals = (
    caseFiles?: CaseFilesType | null
  ): CaseFilesType | null | undefined =>
    caseFiles
      ? payload.reduce(
          (acc, tdoId) => mapDeletedCaseFiles(acc, tdoId),
          caseFiles
        )
      : caseFiles;
  pruneRosterEntriesInPlace(state, payload);
  state.selectedFolders = state.selectedFolders.map((folder) =>
    folder.caseFiles
      ? { ...folder, caseFiles: applyRemovals(folder.caseFiles) }
      : folder
  );
  if (state.selectedVirtualFolder?.parent?.caseFiles) {
    state.selectedVirtualFolder = {
      ...state.selectedVirtualFolder,
      parent: {
        ...state.selectedVirtualFolder.parent,
        caseFiles: applyRemovals(state.selectedVirtualFolder.parent.caseFiles),
      },
    };
  }
};

const fetchExportTdoSuccess = (
  state: CaseMediaState,
  action: Action<
    { temporalDataObject: ExportedTdoState },
    { variables: { treeObjectId: TreeObjectId } }
  >
): CaseMediaState => ({
  ...state,
  selectedFolders: [
    ...(state.selectedFolders || []).map((folder: FolderStateType) => {
      if (folder.treeObjectId === action.meta.variables.treeObjectId) {
        folder.exportedTdo = mapExportedTdo(folder, action);
      }
      return folder;
    }),
  ],
});

const mapExportedTdo = (
  folder: FolderStateType,
  action: Action<
    { temporalDataObject: ExportedTdoState },
    { variables: { treeObjectId: TreeObjectId } }
  >
) => {
  let updatedTdo: ExportedTdoState;
  const exportedTdo: ExportedTdoState = {
    ...action.payload?.temporalDataObject,
    parentFolderId: action.meta.variables.treeObjectId,
    downloadAllStarted: false,
  };
  if (folder.exportedTdo) {
    updatedTdo = { ...folder.exportedTdo, ...exportedTdo };
  } else {
    updatedTdo = { ...exportedTdo };
  }
  return { ...updatedTdo };
};

const setDownloadAllStatus = (
  state: CaseMediaState,
  action: {
    payload: ActionDownloadAllStatusPayload;
  }
): CaseMediaState => {
  state.selectedFolders = [
    ...(state.selectedFolders || []).map((folder) => {
      if (folder.treeObjectId === action.payload?.treeObjectId) {
        folder.exportedTdo = {
          ...folder.exportedTdo,
          downloadAllStarted: action.payload?.status,
        };
      }
      return folder;
    }),
  ];
  return state;
};

const fetchMoreRootFoldersSuccess = (
  state: CaseMediaState,
  action: Action<
    { rootFolders: FolderStateType[] },
    { variables: { limit: number; offset: number } }
  >
): CaseMediaState => {
  const { limit, offset } = action.meta.variables;
  const rootFolder = getRootAdminFolder(state, action?.payload?.rootFolders);
  if (rootFolder) {
    const updatedState: CaseMediaState = {
      ...state,
      rootFolder: {
        ...state.rootFolder,
        subfolders: mapFetchMoreRootFolders(state, rootFolder),
        fetchMoreAvailable: rootFolder?.childFolders?.records?.length === limit,
        fetchingSubFolders: false,
        fetchSubFolderLimit: limit,
        fetchSubFolderOffset: offset,
      },
    };
    return updatedState;
  }
  return state;
};

const mapFetchMoreRootFolders = (
  state: CaseMediaState,
  rootFolder: FolderStateType
) => {
  const newRootFoldersList = filteroutIlluminateApp(
    state,
    rootFolder?.childFolders?.records || []
  ).filter(filterSystemFolders);
  const existingList = [...(state.rootFolder?.subfolders || [])];
  return [...existingList, ...newRootFoldersList];
};

const fetchMoreSubFoldersSuccess = (
  state: CaseMediaState,
  action: Action<
    { folder: FolderStateType },
    { variables: ActionFetchMoreSubFoldersRequest }
  >
) => {
  const { folderId } = action.meta.variables;
  const { childFolders } = action.payload.folder;
  const { limit, offset } = action.meta.variables;
  for (const folder of state.selectedFolders) {
    if (folder.id === folderId) {
      const existingList = [...(folder?.subfolders || [])];
      const newSubfolderList = [...(childFolders?.records || [])];
      folder.subfolders = [...existingList, ...newSubfolderList];
      folder.fetchingSubFolders = false;
      folder.fetchMoreAvailable =
        (childFolders?.records?.length || 0) === limit;
      folder.fetchSubFolderOffset = offset;
      folder.fetchSubFolderLimit = limit;
    }
  }
};

const moveFolderSuccess = (
  state: CaseMediaState,
  action: Action<unknown, { variables: { treeObjectId: TreeObjectId } }>
) => {
  const { treeObjectId } = action.meta.variables;
  Object.assign(state, {
    selectedFolders: mapSelectedFolderAfterMove(state, treeObjectId),
    selectedVirtualFolder: mapVirtualAfterMove(state, treeObjectId),
    allExpandedFolders: [],
  });
  return;
};

const getSelectedFolder = (state: CaseMediaState, selectedId: TreeObjectId) =>
  state.selectedFolders.filter((f) => f?.treeObjectId === selectedId)?.[0];

const mapSelectedFolderAfterMove = (
  state: CaseMediaState,
  selectedId: TreeObjectId
) => {
  const movedFolder = getSelectedFolder(state, selectedId);
  if (movedFolder) {
    const updatedSelectedFolders = state.selectedFolders.filter(
      (f) =>
        f.treeObjectId !== selectedId &&
        f.folderPath &&
        movedFolder.folderPath &&
        f.folderPath.length < movedFolder.folderPath.length
    );
    const sortedFolders = (updatedSelectedFolders || []).map((folder) => {
      if (folder.subfolders) {
        folder.subfolders = mapSortedFolders(folder.subfolders);
      }
      return folder;
    });
    return [...(sortedFolders || [])];
  }
  return [...state.selectedFolders];
};

const mapVirtualAfterMove = (
  state: CaseMediaState,
  selectedId: TreeObjectId
): VirtualFolderStateType => {
  const movedFolder = getSelectedFolder(state, selectedId);
  const virtualParentId = state?.selectedVirtualFolder?.parent?.treeObjectId;
  if (movedFolder || virtualParentId === selectedId) {
    return {
      ...state.selectedVirtualFolder,
      parent: undefined,
      virtualFolderName: undefined,
    };
  }
  return state.selectedVirtualFolder;
};

const removeSelectedFolder = (
  state: CaseMediaState,
  { payload: { layer } }: { payload: { layer: number } }
) => {
  const { selectedFolders } = state;
  const updatedState = {
    ...state,
    selectedVirtualFolder: {},
    selectedFolders:
      layer <= 1 || selectedFolders?.length === 0
        ? []
        : selectedFolders.slice(0, layer - 1),
    lastActiveFolderViewFile: null,
    // VE-25098: leaving/collapsing the column tree clears any file selection.
    multiSelectedFiles: [],
    multiSelectCaseId: null,
    multiSelectAnchorId: null,
    pendingRemovalFileIds: [],
  };
  return updatedState;
};

const fetchCaseOrFolderTreeSuccess = (
  state: CaseMediaState,
  action: Action<
    { folder: FolderStateType },
    { variables: ActionNavToCaseMediaPayload }
  >
) => {
  const { folderPath } = action.payload.folder;
  const { folderId, isCase } = action.meta.variables;
  const parents = (folderPath || []).filter(
    (f) =>
      f.id !== state.rootFolderId && ![f.treeObjectId, f.id].includes(folderId)
  );
  const selectedFolder = (folderPath || []).filter((f) =>
    [f.treeObjectId, f.id].includes(folderId)
  )?.[0];
  if (folderPath && folderPath.length > 0) {
    if (selectedFolder && checkIsCaseId(selectedFolder)) {
      return {
        ...state,
        folderTreeFetchState: 'succeeded' as const,
        selectedFolders: [...parents, selectedFolder],
        selectedVirtualFolder: isCase
          ? {
              parent: selectedFolder,
              virtualFolderName: VirtualFolderNames.INPUT,
            }
          : {},
      };
    }
  }
  return { ...state, folderTreeFetchState: 'succeeded' as const };
};

const setNavToCaseMedia = (
  state: CaseMediaState,
  action: {
    payload?: ActionNavToCaseMediaPayload;
  }
): CaseMediaState => {
  if (!action.payload) {
    return state;
  }
  const { folderId, isCase } = action.payload;
  return {
    ...state,
    isNavToCaseMedia: true,
    isNavToCase: isCase,
    navToId: folderId,
  };
};

const clearNavCase = (state: CaseMediaState): CaseMediaState => ({
  ...state,
  isNavToCaseMedia: false,
  isNavToCase: false,
  navToId: undefined,
});

// VE-25873 Unit 3 — set (or clear, on null) the parent request an editor
// session should land back on when the user exits to the folder view.
const setEditorReturnTarget = (
  state: CaseMediaState,
  action: { payload: CaseMediaState['editorReturnTarget'] }
): CaseMediaState => ({
  ...state,
  editorReturnTarget: action.payload ?? null,
});

/**
 * VE-30022 — the folder view is being left for an editor: start the trip.
 *
 * The snapshot is taken HERE rather than passed in because the columns on
 * screen are state, and the caller (an editor's route-init saga) has no better
 * view of them than this reducer does. Both the selected chain and the open
 * request's virtual parent are recorded: on the way back the chain is replayed
 * ancestor-first, and each of those ancestors needs to recognise itself as one
 * of ours just as much as the request does.
 *
 * Unconditional here, because the caller decides WHEN a trip starts: the saga
 * dispatches this only on arriving at an editor from somewhere that is not
 * another editor, so an in-editor file switch leaves the running trip alone.
 */
const markFolderViewLeft = (state: CaseMediaState): CaseMediaState => ({
  ...state,
  folderViewTrip: {
    leftAt: Date.now(),
    folderIds: Array.from(
      new Set(
        [
          ...(state.selectedFolders || []).map((folder) => folder?.id),
          state.selectedVirtualFolder?.parent?.id,
        ].filter((id): id is NonNullable<typeof id> => !!id)
      )
    ),
  },
});

export const clearAllSelectedFolders = (state: CaseMediaState) => ({
  ...state,
  selectedFolders: [],
  selectedVirtualFolder: {},
  // VE-28429: the retained file lists and their timestamps go with the tree —
  // keeping them would let a later visit reuse rows for a tree that was
  // deliberately reset. VE-30022: the trip too, for the same reason — it names
  // the columns a return may put back, and those columns are what is being
  // thrown away here.
  caseContentsRows: {},
  caseContentsLoadedAt: {},
  subFoldersRows: {},
  subFoldersLoadedAt: {},
  folderViewTrip: null,
  lastActiveFolderViewFile: null,
  // VE-25098: reset any folder-view file selection when the tree resets.
  multiSelectedFiles: [],
  multiSelectCaseId: null,
  multiSelectAnchorId: null,
  pendingRemovalFileIds: [],
});

const setSearchTextReducer = (
  state: CaseMediaState,
  { payload }: { payload: string }
) => ({
  ...state,
  searchText: payload,
  // A new query invalidates every cached scope result set.
  searchResultsCache:
    payload === state.searchText ? state.searchResultsCache : {},
});

/**
 * VE-30449: a fresh fetch replaces the request's names outright — the dialog
 * that asked must not check against the previous open's list while this one
 * is in flight. Its own pages then arrive through `appendRequestRoster`.
 */
export const fetchRequestRosterRequest = (
  state: CaseMediaState,
  { payload }: ReturnType<typeof FETCH_REQUEST_ROSTER_REQUEST>
) => ({
  ...state,
  // Only the request in view is kept. A roster is one entry per file, so
  // holding every request visited this session would grow without bound, and
  // nothing reads a roster for a request that is not open.
  requestRoster: {
    [payload.caseId]: {
      status: 'loading' as const,
      entries: [],
      requestId: payload.requestId,
    },
  },
});

/**
 * A complete roster built from page one, with no walk behind it (VE-32420).
 *
 * `setRequestRoster` drops anything that does not answer an in-flight fetch,
 * which is right for a walk's answer and wrong for this: nothing was fetched,
 * so there is no entry to match and the publish was silently discarded —
 * leaving every request that fits one page with no roster at all, and Select
 * All disabled for the common case.
 *
 * Replaces the map rather than merging into it, for the same two reasons
 * `fetchRequestRosterRequest` does: only the request in view is kept, and a
 * fresh `requestId` means a page still in flight from an earlier walk no
 * longer matches and is ignored.
 */
export const publishRequestRoster = (
  state: CaseMediaState,
  { payload }: ReturnType<typeof PUBLISH_REQUEST_ROSTER>
) => ({
  ...state,
  requestRoster: {
    [payload.caseId]: {
      status: 'ready' as const,
      entries: payload.entries,
      requestId: payload.requestId,
    },
  },
});

/** Dropped unless it answers the request's latest fetch. */
export const setRequestRoster = (
  state: CaseMediaState,
  { payload }: ReturnType<typeof SET_REQUEST_ROSTER>
) =>
  state.requestRoster[payload.caseId]?.requestId !== payload.requestId
    ? state
    : {
        ...state,
        requestRoster: {
          ...state.requestRoster,
          [payload.caseId]: {
            status: payload.status,
            entries: payload.entries,
            requestId: payload.requestId,
          },
        },
      };

/** A page of the latest read, while it is still loading; anything else is dropped. */
export const appendRequestRoster = (
  state: CaseMediaState,
  { payload }: ReturnType<typeof APPEND_REQUEST_ROSTER>
) => {
  const entry = state.requestRoster[payload.caseId];
  if (entry?.requestId !== payload.requestId || entry.status !== 'loading') {
    return state;
  }
  return {
    ...state,
    requestRoster: {
      ...state.requestRoster,
      [payload.caseId]: {
        ...entry,
        entries: [...entry.entries, ...payload.entries],
      },
    },
  };
};

/**
 * VE-32420: merge per-row data fetched for a window of ids.
 *
 * UPSERT BY ID, into the row objects already there — a row may have picked up
 * in-place updates (a status change, a refreshed output) since it was last
 * fetched, and those live on the SAME fields this is filling in. Spreading the
 * fetched row over the existing one would undo them; spreading the existing
 * one over the fetched one keeps them, at the cost of a column being one
 * refresh stale, which is what every other in-place patch here already
 * accepts.
 *
 * Rows the list does not hold yet are APPENDED, not dropped — reaching files
 * beyond the first page is the whole point of the window fetch, so most of
 * what comes back is new here. Nothing is resurrected by that: the list
 * renders in ROSTER order, so a row the roster does not name is never drawn.
 */
export const mergeCaseFileRows = (
  state: CaseMediaState,
  { payload }: ReturnType<typeof MERGE_CASE_FILE_ROWS>
) => {
  const parent = state.selectedVirtualFolder?.parent;
  const existing = parent?.caseFiles?.inputFiles;
  if (!existing?.length || !payload.rows.length) {
    return state;
  }
  // Answers the request that is still open, or not at all. See the action.
  if (parent?.id !== payload.caseId) {
    return state;
  }
  const fetchedById = new Map(
    payload.rows.filter((row) => !!row?.id).map((row) => [String(row.id), row])
  );
  if (!fetchedById.size) {
    return state;
  }
  const seen = new Set<string>();
  const merged = existing.map((file) => {
    const key = String(file.id);
    const fetched = fetchedById.get(key);
    if (!fetched) {
      return file;
    }
    seen.add(key);
    return { ...fetched, ...file };
  });
  /**
   * APPEND the rows that were not here yet — which is most of them.
   *
   * This holds the rows the per-row query has fetched, and the window fetch
   * exists precisely to reach files BEYOND that: a row at position 400 was
   * never in this list, so an update-only merge matched nothing and threw the
   * answer away, leaving those rows spinning forever.
   *
   * Appending cannot resurrect anything: the list renders in ROSTER order
   * (`mergeRosterWithLoaded`), so a row here that the roster does not name —
   * trashed, moved, or an export — is simply never drawn.
   */
  for (const [key, row] of fetchedById) {
    if (!seen.has(key)) {
      merged.push(row);
    }
  }
  /**
   * The request's files live in state TWICE — on the selected virtual folder
   * (what the list reads) and on the matching entry in the `selectedFolders`
   * chain (what `selectedFolder` consumers read, Download All among them).
   * Every handler that touches one touches the other; updating only the
   * virtual folder left Download All listing the first page's outputs however
   * far the user had scrolled, because the rows carrying the rest never
   * reached the copy it reads.
   *
   * `outputFiles` is DERIVED from the rows and recomputed alongside them, the
   * same way `patchFolderFiles` does it — the output assets arrive on exactly
   * the rows this merge is adding.
   */
  const mergedCaseFiles = (
    caseFiles: CaseFilesType | null | undefined
  ): CaseFilesType => ({
    ...caseFiles,
    inputFiles: merged,
    ...(caseFiles?.outputFiles
      ? { outputFiles: mapOutputMediaFiles(merged) }
      : {}),
  });

  return {
    ...state,
    selectedFolders: (state.selectedFolders || []).map((folder) =>
      folder?.id === parent?.id && folder?.caseFiles
        ? { ...folder, caseFiles: mergedCaseFiles(folder.caseFiles) }
        : folder
    ),
    selectedVirtualFolder: {
      ...state.selectedVirtualFolder,
      parent: {
        ...parent,
        caseFiles: mergedCaseFiles(parent?.caseFiles),
      },
    },
  } as CaseMediaState;
};

const setFolderPermissions = (
  state: CaseMediaState,
  { payload }: { payload: Record<string, FolderPermissionFlags> }
) => ({
  ...state,
  folderPermissions: { ...state.folderPermissions, ...payload },
});

const clearAllSearchResults = (state: CaseMediaState) => ({
  ...state,
  searchResults: [],
  searchMoreFilesOffset: 0,
  searchMoreFoldersOffset: 0,
  searchMoreFilesAvailable: false,
  searchMoreFoldersAvailable: false,
  isSearchMoreResults: false,
  isSearchInProgress: false,
});

const setSearchScope = (
  state: CaseMediaState,
  {
    payload,
  }: {
    payload: {
      folderId: string;
      folderName: string;
      treeObjectId?: TreeObjectId;
      isCase?: boolean;
    };
  }
) => ({
  ...state,
  searchScopeFolderId:
    payload.folderId as CaseMediaState['searchScopeFolderId'],
  searchScopeTreeObjectId: payload.treeObjectId ?? null,
  searchScopeFolderName: payload.folderName,
  searchScopeIsCase: payload.isCase ?? false,
});

const clearSearchScope = (state: CaseMediaState) => ({
  ...state,
  searchScopeFolderId: null,
  searchScopeTreeObjectId: null,
  searchScopeFolderName: null,
  searchScopeIsCase: false,
});

const setSearchBarScopeMode = (
  state: CaseMediaState,
  { payload }: { payload: CaseMediaState['searchBarScopeMode'] }
) => ({
  ...state,
  searchBarScopeMode: payload,
});

const openSearchDialog = (state: CaseMediaState) => ({
  ...state,
  isSearchDialogOpen: true,
});

const closeSearchDialog = (state: CaseMediaState) => ({
  ...state,
  isSearchDialogOpen: false,
});

const searchCasesAndFoldersInit = (state: CaseMediaState) => ({
  ...state,
  isSearchInProgress: true,
});

const setSearchMoreResults = (state: CaseMediaState) => ({
  ...state,
  isSearchMoreResults: true,
});

const searchCasesAndFoldersFailure = (state: CaseMediaState) => ({
  ...state,
  isSearchInProgress: false,
});

const setSearchResultsReducer = (
  state: CaseMediaState,
  action: {
    payload: {
      searchResults: CaseMediaState['searchResults'];
      searchMoreFilesOffset: number;
      searchMoreFoldersOffset: number;
      searchMoreFilesAvailable: boolean;
      searchMoreFoldersAvailable: boolean;
      cacheKey?: string;
    };
  }
) => {
  const {
    searchResults,
    searchMoreFilesOffset,
    searchMoreFoldersOffset,
    searchMoreFilesAvailable,
    searchMoreFoldersAvailable,
    cacheKey,
  } = action.payload;

  const orderedResults = mapOrderedResults(searchResults);

  return {
    ...state,
    searchResults: orderedResults,
    searchMoreFilesOffset: searchMoreFilesOffset,
    searchMoreFoldersOffset: searchMoreFoldersOffset,
    searchMoreFilesAvailable: searchMoreFilesAvailable,
    searchMoreFoldersAvailable: searchMoreFoldersAvailable,
    isSearchMoreResults: false,
    isSearchInProgress: false,
    searchResultsCache: cacheKey
      ? {
          ...state.searchResultsCache,
          [cacheKey]: {
            searchResults: orderedResults,
            searchMoreFilesOffset,
            searchMoreFoldersOffset,
            searchMoreFilesAvailable,
            searchMoreFoldersAvailable,
          },
        }
      : state.searchResultsCache,
  };
};

const restoreSearchResultsReducer = (
  state: CaseMediaState,
  { payload }: { payload: string }
) => {
  const cached = state.searchResultsCache[payload];
  if (!cached) {
    return state;
  }
  return {
    ...state,
    searchResults: cached.searchResults,
    searchMoreFilesOffset: cached.searchMoreFilesOffset,
    searchMoreFoldersOffset: cached.searchMoreFoldersOffset,
    searchMoreFilesAvailable: cached.searchMoreFilesAvailable,
    searchMoreFoldersAvailable: cached.searchMoreFoldersAvailable,
    isSearchMoreResults: false,
    isSearchInProgress: false,
  };
};

const isNameExist = (
  item: SearchResultFolder | SearchResultFile
): item is SearchResultFolder =>
  'caseName' in item && item.caseName !== undefined;

const isCaseFolder = (item: SearchResultFolder) =>
  item?.contentTemplates && item.contentTemplates.length > 0;

const mapOrderedResults = (
  results: CaseMediaState['searchResults']
): (SearchResultFolder | SearchResultFile)[] => {
  const filteredFolders = (results || []).filter(
    (res: SearchResultFolder | SearchResultFile): res is SearchResultFolder =>
      isNameExist(res) && !isCaseFolder(res)
  );

  const sortedFolders = _sortBy(filteredFolders, [
    (i) => i.caseName?.toLowerCase(),
  ]);

  const filteredCases = (results || []).filter(
    (res: SearchResultFolder | SearchResultFile): res is SearchResultFolder =>
      isNameExist(res) && !!isCaseFolder(res)
  );

  const sortedCases = _sortBy(filteredCases, [
    (i) => i.caseName?.toLowerCase(),
  ]);

  const filteredFiles = (results || []).filter(
    (res: SearchResultFolder | SearchResultFile): res is SearchResultFile =>
      !isNameExist(res)
  );

  const sortedFiles = _sortBy(filteredFiles, [
    (i) => i.filename?.toLowerCase(),
  ]);

  return [
    ...(sortedFolders || []),
    ...(sortedCases || []),
    ...(sortedFiles || []),
  ];
};

const fetchMoreFilesInit = (state: CaseMediaState) => ({
  ...state,
  selectedVirtualFolder: {
    ...state.selectedVirtualFolder,
    fetchingFiles: true,
    parent: {
      ...state.selectedVirtualFolder.parent,
      fetchingSubFolders: true,
    },
  },
});

const getAssetFiles = (
  existingFiles?: AssetFileType[],
  newFiles?: AssetFileType[]
) => {
  if (!existingFiles && !newFiles) {
    return undefined;
  }
  if (newFiles && newFiles.length > 0) {
    if (!(existingFiles && existingFiles.length > 0)) {
      return [...newFiles];
    }
    // VE-32420: dedupe by id rather than appending blind. Under Folders v2 the
    // `childTDOs` resolver pages with LIMIT/OFFSET and no ORDER BY, so the same
    // TDO can be returned on more than one page — which showed up as a file
    // appearing several times in one request. Keeping the EXISTING copy
    // matters: it may carry in-place updates (a status change, a refreshed
    // output) that the newly fetched page predates.
    const seen = new Set(existingFiles.map((file) => file.id));
    return [...existingFiles, ...newFiles.filter((file) => !seen.has(file.id))];
  }
  return existingFiles && existingFiles.length > 0
    ? [...existingFiles]
    : undefined;
};

const mapFetchMoreFiles = (
  state: CaseMediaState,
  selectedId: string,
  action: Action<
    { folder: FolderStateType },
    { variables: ActionFetchMoreFilesRequest }
  >
) => {
  const { childTDOs } = action.payload.folder;
  const { limit, offset } = action.meta.variables;

  if (childTDOs?.records) {
    const udpatedSelectedFolders = (state.selectedFolders || []).map(
      (folder) => {
        if (folder?.id === selectedId) {
          folder.fetchMoreAvailable = childTDOs?.records?.length === limit;
          folder.fetchingSubFolders = false;
          folder.fetchSubFolderLimit = limit;
          folder.fetchSubFolderOffset = offset;
          const newCaseFiles = mapMediaFiles(childTDOs?.records || []);

          if (newCaseFiles) {
            if (folder.caseFiles) {
              folder.caseFiles = {
                inputFiles: getAssetFiles(
                  folder.caseFiles.inputFiles,
                  newCaseFiles.inputFiles
                ),
                outputFiles: getAssetFiles(
                  folder.caseFiles.outputFiles,
                  newCaseFiles.outputFiles
                ),
              };
            } else {
              folder.caseFiles = { ...newCaseFiles };
            }
          }
        }
        return folder;
      }
    );
    return [...udpatedSelectedFolders];
  }
  return [...state.selectedFolders];
};

const fetchMoreFilesSuccess = (
  state: CaseMediaState,
  action: Action<
    { folder: FolderStateType },
    { variables: ActionFetchMoreFilesRequest }
  >
) => {
  const { caseId } = action.meta.variables;
  state.selectedFolders = mapFetchMoreFiles(state, caseId, action);
  state.selectedVirtualFolder = refreshSelectedVirtualFolder(state, true);
};

const fetchMoreFilesFailure = (state: CaseMediaState) => ({
  ...state,
  selectedVirtualFolder: {
    ...state.selectedVirtualFolder,
    fetchingFiles: false,
    parent: {
      ...state.selectedVirtualFolder.parent,
      fetchingSubFolders: false,
    },
  },
});

const setShowLegacyMedias = (
  state: CaseMediaState,
  action: {
    payload: ActionSetShowLegacyMediasPayload;
  }
) => {
  const { isShowLegacyMedias } = action.payload;
  return {
    ...state,
    isShowLegacyMedias: isShowLegacyMedias,
  };
};

const fetchLegacyMediasInit = (state: CaseMediaState) => ({
  ...state,
  isFetchingLegacyMedias: true,
});

/** Mark an enrichment batch as attempted, whichever way it went. */
const markLegacyTdoDetailAttempted = (
  state: CaseMediaState,
  tdoIds: TDOId[] | undefined
): CaseMediaState['legacyTdoDetailAttempted'] => {
  if (!tdoIds?.length) {
    return state.legacyTdoDetailAttempted;
  }
  const attempted = { ...state.legacyTdoDetailAttempted };
  tdoIds.forEach((tdoId) => {
    attempted[`id_${tdoId}`] = true;
  });
  return attempted;
};

/**
 * The list is still loading while any SDO has no attempt recorded. Checking
 * that, rather than clearing on the first batch, is what makes the spinner wait
 * for the LAST of several parallel enrichment batches.
 */
const isEnrichmentOutstanding = (
  medias: LegacyMediaDetailType[],
  attempted: CaseMediaState['legacyTdoDetailAttempted']
): boolean => medias.some((m) => !attempted[`id_${m.tdoId}`]);

const fetchTdoDetailForSdosFailure = (
  state: CaseMediaState,
  action: { meta?: { variables?: { tdoIds?: TDOId[] } } }
) => {
  const legacyTdoDetailAttempted = markLegacyTdoDetailAttempted(
    state,
    action.meta?.variables?.tdoIds
  );
  return {
    ...state,
    legacyTdoDetailAttempted,
    isFetchingLegacyMedias: isEnrichmentOutstanding(
      state.LegacyMedias || [],
      legacyTdoDetailAttempted
    ),
  };
};

const fetchLegacyMediasSuccess = (
  state: CaseMediaState,
  action: Action<
    ActionLegacyMediasSuccessPayload,
    { variables: ActionFetchLegacyMediasRequest }
  >
) => {
  const { structuredDataObjects } = action.payload;
  const { limit, offset } = action.meta.variables;
  /**
   * VE-32420: ONLY A PAGE THAT CONTINUES THE READ IN STATE.
   *
   * The sort is the server's ordering, and changing it starts a new read from
   * offset 0 — but a scroll page already in flight is not cancellable (the
   * `takeLatest` cancels the saga, not the thunk) and SUCCESS is taken by
   * `takeEvery`. Landing after the new read, it used to take the append branch
   * below: rows ordered by the PREVIOUS sort appended to the new ordering's
   * first page, and `fetchMoreLegacyMediasOffset` dragged up to its offset, so
   * the next scroll skipped every page in between. Silently — a chunk of the
   * list simply never appeared.
   *
   * Every caller either starts a read at offset 0 or continues one at exactly
   * `fetchMoreLegacyMediasOffset + limit`, so anything else is answering a read
   * that has been replaced. Offset-based rather than length-based because the
   * dedupe below can legitimately make the list shorter than its offset.
   */
  const continuesCurrentRead =
    offset === 0 || offset === (state.fetchMoreLegacyMediasOffset ?? 0) + limit;
  if (!continuesCurrentRead) {
    return state;
  }
  const existingMedias = [...(state.LegacyMedias || [])];
  const newListOfMedias = (structuredDataObjects?.records || [])
    .filter((s) => s.data?.tdoId)
    .map((m) => {
      const mediaDetail: LegacyMediaDetailType = {
        ...m.data,
        sdoId: m.id,
        tdoName: m.data.tdoId,
        createdDateTime: m.createdDateTime,
        modifiedDateTime: m.modifiedDateTime,
      };
      return mediaDetail;
    });

  // VE-26943: dedupe by sdoId when appending. The SDO list is ordered by
  // `modifiedDateTime desc` and a status edit bumps that field, so a record can
  // move between pages while the user is paging — appending blind would show it
  // twice. (Rows shifting the other way can still be skipped; only a stable
  // sort key on the server would fix that.)
  const appended = [...existingMedias, ...newListOfMedias];
  return {
    ...state,
    LegacyMedias:
      offset === 0
        ? [...newListOfMedias]
        : [...new Map(appended.map((item) => [item.sdoId, item])).values()],
    fetchMoreLegacyMediasAvailable:
      structuredDataObjects?.records?.length === limit,
    fetchMoreLegacyMediasOffset: offset,
    hasFetchedLegacyMedias: true,
    isFetchingLegacyMedias:
      (newListOfMedias?.length || 0) < 1 ? false : state.isFetchingLegacyMedias,
  };
};

/**
 * VE-26943: drop a file from the unassigned list in place.
 *
 * Deletes and moves used to trigger a full refetch from offset 0, which under
 * the branch's demand-paging REPLACES the list with page one — a user who had
 * scrolled in 150 files lost 120 of them for deleting one. Removing the row
 * locally is also what the case path does (removeCaseInputFiles).
 */
const removeUnassignedFileInPlace = (
  state: CaseMediaState,
  match: (media: LegacyMediaDetailType) => boolean
): void => {
  const removed = (state.LegacyMedias || []).filter(match);
  if (removed.length === 0) {
    return;
  }
  state.LegacyMedias = (state.LegacyMedias || []).filter((m) => !match(m));
  removed.forEach((m) => {
    delete state.legacyTdoDetails[`id_${m.tdoId}`];
  });
};

const fetchLegacyMediasFailure = (state: CaseMediaState) => ({
  ...state,
  hasFetchedLegacyMedias: true,
  isFetchingLegacyMedias: false,
});

const fetchTdoDetailForSdosSuccess = (
  state: CaseMediaState,
  action: {
    payload: Record<string, TemporalDataObject>;
    meta?: { variables?: { tdoIds?: TDOId[] } };
  }
) => {
  const legacyMedias = state.LegacyMedias || [];
  const tdos = action.payload;
  // VE-26943: keep the raw TDO records so the synthetic Unassigned Media folder
  // can be derived from (SDO + TDO) rather than maintained as a second list.
  // Merged, not replaced: the enrichment is fetched in batches, so each SUCCESS
  // carries only part of a page.
  const legacyTdoDetails = isEmpty(tdos)
    ? state.legacyTdoDetails
    : {
        ...state.legacyTdoDetails,
        ...(tdos as unknown as Record<string, UnassignedTdoRecord>),
      };
  const legacyTdoDetailAttempted = markLegacyTdoDetailAttempted(
    state,
    action.meta?.variables?.tdoIds
  );
  if (legacyMedias.length > 0 && !isEmpty(tdos)) {
    const updatedMedias = legacyMedias
      .filter((m) => {
        const parentFolderName = tdos[`id_${m.tdoId}`]?.folders?.[0]?.name;
        if (parentFolderName === SYSTEM_FOLDER_REDACT_TRASH) {
          return false;
        }
        return true;
      })
      .map((m) => {
        const tdo = tdos[`id_${m.tdoId}`];
        if (tdo) {
          const updatedSdo: LegacyMediaDetailType = {
            ...m,
            tdoName: tdo.name as string,
            // TODO: Can we type this?
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            fileType: tdo.details?.veritoneFile?.mimetype,
            jobs: tdo.jobs,
            // TODO: Can we type this? or introspect to validate the type?
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            details: tdo?.details,
          };
          if (tdo.folders?.[0]?.id) {
            updatedSdo.parentFolderId = tdo.folders[0].id as CaseId;
          }
          return updatedSdo;
        }
        return m;
      });

    return {
      ...state,
      isFetchingLegacyMedias: isEnrichmentOutstanding(
        updatedMedias,
        legacyTdoDetailAttempted
      ),
      LegacyMedias: updatedMedias,
      legacyTdoDetails,
      legacyTdoDetailAttempted,
    };
  }
  // VE-27388: a batch whose TDOs all resolved to null still SETTLES the rows it
  // asked about, so the loading flag has to be recomputed here too. Returning
  // early without it left the spinner up forever whenever the last batch of a
  // page came back empty (orphan SDOs whose TDO was deleted underneath them).
  return {
    ...state,
    isFetchingLegacyMedias: isEnrichmentOutstanding(
      legacyMedias,
      legacyTdoDetailAttempted
    ),
    legacyTdoDetails,
    legacyTdoDetailAttempted,
  };
};

const renameFolderSuccess = (
  state: CaseMediaState,
  action: {
    payload: { updateFolder: ActionRenameFolderPayload };
  }
) => {
  const { id, name } = action.payload.updateFolder;
  state.rootFolder = mapRenamedFolder({ ...state.rootFolder }, id, name);
  state.selectedFolders = (state.selectedFolders || []).map((folder) =>
    mapRenamedFolder(folder, id, name)
  );
  // The folder tree above is the source of truth for names, but each loaded
  // file also carries a cached breadcrumb copy of its ancestor names
  // (inputFiles[].folders[].folderPath). That breadcrumb is stamped at fetch
  // time and is what performDeleteFile persists into the Trash "Original
  // Location" tag. Re-sync it here so deleting a file right after a rename
  // records the new name instead of the stale pre-rename one (VE-25177).
  renameFolderInInputFileBreadcrumbs(state, id, name);
  // Deleting a folder/request/sub-folder itself stamps a *different* cached
  // breadcrumb into the same Trash tag: each folder-tree node carries its own
  // `folderPath` (its ancestor chain), and FolderColumn builds `oldFolderPath`
  // from `selectedFolder.subfolders[].folderPath`. mapRenamedFolder above only
  // updates node names, never these breadcrumb copies — so renaming a parent
  // then deleting a child folder recorded the stale parent name. Re-sync the
  // folder-tree breadcrumbs too (VE-25177).
  renameFolderInTreeBreadcrumbs(state.rootFolder, id, name);
  (state.selectedFolders || []).forEach((folder) =>
    renameFolderInTreeBreadcrumbs(folder, id, name)
  );
  return;
};

// Shared core for both breadcrumb caches below: rename any entries in a single
// `folderPath` chain (and their `parent` refs) that point at the renamed folder.
// The entry types differ slightly between the two caches (tree nodes type
// `parent` as `{ id }`; file breadcrumbs carry `{ id, name }`), so the param is
// kept structural — writing `name` onto a parent that lacked one is harmless.
const renameFolderInBreadcrumbPath = (
  folderPath:
    | Array<{
        id?: string;
        name?: string;
        parent?: { id?: string; name?: string } | null;
      }>
    | undefined,
  folderId: FolderId,
  name: string
): void => {
  folderPath?.forEach((entry) => {
    if (entry.id === folderId) {
      entry.name = name;
    }
    if (entry.parent?.id === folderId) {
      entry.parent.name = name;
    }
  });
};

// Propagate a folder rename into the cached per-file breadcrumbs so they stay
// consistent with the folder tree — these feed the Trash "Original Location"
// when a *file* is deleted (performDeleteFile).
const renameFolderInInputFileBreadcrumbs = (
  state: CaseMediaState,
  folderId: FolderId,
  name: string
): void => {
  const inputFiles = state.selectedVirtualFolder?.parent?.caseFiles?.inputFiles;
  if (!inputFiles?.length) {
    return;
  }
  inputFiles.forEach((file) => {
    file.folders?.forEach((folder) => {
      // VE-21114: the folder's own (leaf) name. v2 excludes the leaf from
      // folderPath, so the Trash "Original Location" re-attaches it from
      // `folders[].name` (tdoFolderChain) — keep it synced on rename too, or a
      // delete right after a rename would persist the stale leaf name under v2.
      if (folder.id === folderId) {
        folder.name = name;
      }
      renameFolderInBreadcrumbPath(folder.folderPath, folderId, name);
    });
  });
};

// Propagate a folder rename into the cached `folderPath` breadcrumbs carried by
// every folder-tree node (rootFolder / selectedFolders and their descendants).
// These breadcrumbs are what FolderColumn stamps into the Trash "Original
// Location" when a folder/request/sub-folder is deleted, so a renamed ancestor
// must be refreshed here or the delete would persist its stale name (VE-25177).
const renameFolderInTreeBreadcrumbs = (
  node: FolderStateType | null | undefined,
  folderId: FolderId,
  name: string
): void => {
  if (!node) {
    return;
  }
  renameFolderInBreadcrumbPath(node.folderPath, folderId, name);
  node.subfolders?.forEach((sub) =>
    renameFolderInTreeBreadcrumbs(sub, folderId, name)
  );
};

const mapRenamedFolder = (
  folder: FolderStateType,
  folderId: FolderId,
  name: string
): FolderStateType => {
  if (folder?.id === folderId) {
    folder.name = name;
  } else {
    const subfolders = [...(folder.subfolders || [])];
    folder.subfolders = mapSortedFolders([
      ...subfolders.map((sub: FolderStateType) => {
        if (sub.id === folderId) {
          sub.name = name;
        }
        return sub;
      }),
    ]);
  }
  return folder;
};

const setSourceFolderToMoveRequest = (
  state: CaseMediaState,
  action: {
    payload: {
      sourceFolderToMove: FolderStateType;
      moveFromFolder: FolderStateType;
    };
  }
) => {
  const { sourceFolderToMove, moveFromFolder } = action.payload;
  return {
    ...state,
    sourceFolderToMove: sourceFolderToMove,
    moveFromFolder: moveFromFolder,
  };
};

const clearAllMoveStateRequest = (state: CaseMediaState) => ({
  ...state,
  sourceFolderToMove: undefined,
  moveFromFolder: undefined,
  allExpandedFolders: [],
});

const setMoveExpandedFolderRequest = (
  state: CaseMediaState,
  action: {
    payload: { expandedFolder: FolderStateType };
  }
) => {
  const { expandedFolder } = action.payload;
  return {
    ...state,
    allExpandedFolders: [...(state?.allExpandedFolders || []), expandedFolder],
  };
};

const clearFromMoveExpandedRequest = (
  state: CaseMediaState,
  action: {
    payload: { folderId: FolderId };
  }
) => {
  const { folderId } = action.payload;
  return {
    ...state,
    allExpandedFolders: [
      ...((state.allExpandedFolders || []).filter(
        (folder: FolderStateType) => folder.id !== folderId
      ) || []),
    ],
  };
};

const fetchForExpandedFolderInit = (
  state: CaseMediaState,
  action: Action<undefined, { variables: ActionFetchForExportedFolderPayload }>
) => {
  const { folderId } = action.meta.variables;
  state.allExpandedFolders = (state.allExpandedFolders || []).map((folder) => {
    if (folder.id === folderId) {
      folder.fetchForExpandInprogress = true;
    }
    return folder;
  });
  return;
};
const fetchForExpandedFolderSuccess = (
  state: CaseMediaState,
  action: Action<
    { folder: FolderStateType },
    { variables: ActionFetchForExportedFolderPayload }
  >
) => {
  const { childFolders } = action.payload.folder;
  const { folderId, offset, limit } = action.meta.variables;
  state.allExpandedFolders = (state.allExpandedFolders || []).map((folder) => {
    if (folder.id === folderId) {
      // Record the result even when it's empty: an empty folder must land as
      // `subfolders: []` (and clear the in-progress flag) or consumers that
      // key on "subfolders known yet?" — the grid-mode panel spinner, the
      // Move dialog's expand indicator — spin forever on empty folders.
      const newList = [...(childFolders?.records || [])];
      folder.fetchForExpandInprogress = newList.length === limit;
      const existing = [...(folder.subfolders || [])];
      folder.subfolders = offset > 0 ? [...existing, ...newList] : [...newList];
    }
    return folder;
  });
  return;
};

const fetchForExpandedFolderFailure = (
  state: CaseMediaState,
  action: Action<
    [{ message: string }],
    { variables: ActionFetchForExportedFolderPayload }
  >
) => {
  const { folderId } = action.meta.variables;
  state.allExpandedFolders = (state.allExpandedFolders || []).map((folder) => {
    if (folder.id === folderId) {
      folder.fetchForExpandInprogress = false;
    }
    return folder;
  });
  return;
};

const setSourceFileToMoveRequest = (
  state: CaseMediaState,
  action: {
    payload: ActionSetSourceFileToMovePayload;
  }
) => {
  const { sourceFileToMove, moveFromFolder } = action.payload;
  return {
    ...state,
    sourceFileToMove: sourceFileToMove,
    moveFromFolder: moveFromFolder,
  };
};

const clearSourceFileToMove = (state: CaseMediaState) => ({
  ...state,
  sourceFileToMove: undefined,
  moveFromFolder: undefined,
  allExpandedFolders: [],
});

const setSelectedRequestToIngest = (
  state: CaseMediaState,
  action: {
    payload: { selectedRequestToIngest: FolderId };
  }
) => ({
  ...state,
  // TODO: Not really sure where this should be changed to clean this up FolderId vs CaseId
  selectedRequestToIngest: action.payload?.selectedRequestToIngest as CaseId,
});

const clearSelectedRequestToIngest = (state: CaseMediaState) => ({
  ...state,
  selectedRequestToIngest: undefined,
});

/**
 * Exported for its own test: one status change has to reach three copies of
 * the same file, and the only honest way to check that is to call this.
 */
export const updateStatusChangeSuccess = (
  state: CaseMediaState,
  action: {
    payload: ActionUpdateRedactionStatusChangePayload;
  }
) => {
  const { tdoId, redactionStatus, redactionStatusLabel, redactionStatusKey } =
    action.payload;
  // Store the human name + custom UUID on the tag (VE-24546) so the folder-view
  // pill resolves the live label and, for a deleted status, its frozen name.
  const statusWriteFields = redactionStatusLabel
    ? {
        redactionStatus: redactionStatusLabel,
        ...(redactionStatusKey ? { redactionStatusKey } : {}),
      }
    : getBuiltInRedactionStatusTagWriteFields(redactionStatus);
  const caseFiles = cloneDeep(state.selectedVirtualFolder.parent?.caseFiles);
  const fileInputUpdate: AssetFileType[] = [];
  caseFiles?.inputFiles?.forEach((file) => {
    if (file.id === tdoId) {
      const updateFile = file;
      updateFile.details = file.details || {};

      const existingTags = updateFile?.details?.tags || [];
      updateFile.details.tags = applyStatusTagWrite(
        existingTags,
        statusWriteFields
      );

      fileInputUpdate.push(updateFile);
    } else {
      fileInputUpdate.push(file);
    }
  });
  return {
    ...state,
    // VE-26943: Unassigned Media's rows are DERIVED from legacyTdoDetails, so
    // the write above (into the stored stub) is thrown away by the selector
    // substitution — the pill kept showing the old status for the rest of the
    // session. Write the same tag into the TDO record the derivation reads.
    legacyTdoDetails: updateLegacyTdoStatusTag(
      state.legacyTdoDetails,
      tdoId,
      statusWriteFields
    ),
    // AND THE COLUMN CHAIN, which is where the EDITOR reads the same files.
    //
    // `selectEditorRequestDocuments` resolves the request's folder out of
    // `selectedFolders`, not out of `selectedVirtualFolder` — so a status
    // written only above lands where the folder view looks and nowhere the
    // editor does. The document panel's rows and the merged document's
    // summary both went on showing the previous status until the page was
    // reloaded.
    //
    // A NEW REFERENCE IS THE POINT, not just new data. The loop above assigns
    // into the file object it found, so the two copies may already be the
    // same object and already carry the new tag — but the selector is
    // memoised on `selectedFolders`, and without a fresh reference nothing
    // recomputes and nothing re-renders. This is the third place one status
    // has to be written (see `legacyTdoDetails` above); they are three
    // derivations of one fact, and the fact has to reach all of them.
    selectedFolders: updateFoldersStatusTag(
      state.selectedFolders,
      tdoId,
      statusWriteFields
    ),
    selectedVirtualFolder: {
      ...state.selectedVirtualFolder,
      parent: {
        ...state.selectedVirtualFolder.parent,
        caseFiles: {
          ...state.selectedVirtualFolder.parent?.caseFiles,
          inputFiles: fileInputUpdate,
        },
      },
    },
  };
};

/**
 * Apply a status tag to the matching file in every selected folder's file
 * list, returning the SAME array when no folder holds it — so an ordinary
 * folder-view change does not invalidate every memoised selector keyed on
 * `selectedFolders`.
 */
const updateFoldersStatusTag = (
  folders: CaseMediaState['selectedFolders'],
  tdoId: string,
  statusWriteFields: ReturnType<typeof getBuiltInRedactionStatusTagWriteFields>
): CaseMediaState['selectedFolders'] => {
  let touched = false;
  const next = (folders || []).map((folder) => {
    const inputFiles = folder?.caseFiles?.inputFiles;
    if (!inputFiles?.some((file) => file.id === tdoId)) {
      return folder;
    }
    touched = true;
    return {
      ...folder,
      caseFiles: {
        ...folder.caseFiles,
        inputFiles: inputFiles.map((file) =>
          file.id === tdoId
            ? {
                ...file,
                details: {
                  ...file.details,
                  tags: applyStatusTagWrite(
                    file.details?.tags || [],
                    statusWriteFields
                  ),
                },
              }
            : file
        ),
      },
    };
  });
  return touched ? next : folders;
};

/**
 * Apply a status write to one file's tag list — ADDING the tag when the file
 * has none.
 *
 * The add is the whole point. A file that has never had a status set carries
 * no `redactionStatus` tag at all, and every surface reads that absence as To
 * Do: the status filter, the status sort, and the merged document's summary
 * all say so. So "map the existing tag" silently does nothing for exactly the
 * files a freshly ingested request is made of — the server accepts the write,
 * the toast reports success, and the rows stay To Do until a reload.
 *
 * One helper for all three copies of a file (the open virtual folder, the
 * column chain, and the unassigned TDO record) because they had three
 * transcriptions of this rule and only one of them handled the absent tag.
 *
 * WHICH TAG IS THE STATUS TAG — `value === IN_REDACTION_TAG_KEY`, the rule
 * every reader uses and the rule the server write uses. The three copies
 * tested for a `redactionStatus` KEY instead, and once the add branch was
 * shared that difference became a bug: a file soft-deleted while it had no
 * tags gets `{ value: 'in redaction', toBeDeletedTime, … }` (see the trash
 * saga) — an in-redaction tag with no `redactionStatus`. The server merged
 * into it by `value`; key-presence did not, appended a SECOND in-redaction
 * tag, and readers `.find()` the first and see no status at all. The row read
 * To Do after a write that succeeded.
 *
 * Key presence is NOT kept as an OR. It looks like harmless insurance for a
 * legacy tag holding a status under some other `value`, and it is the same
 * class of bug in mirror image: `performUpdateRedactionStatus` matches on
 * `value` alone, so for such a tag the server would APPEND a new in-redaction
 * tag while this merged into the legacy one and appended nothing — local
 * state with no status tag at all, every reader showing To Do, and the next
 * bulk run rewriting the file as a change that never happened. No writer in
 * this repo produces such a tag anyway: every creation site passes
 * `value: IN_REDACTION_TAG_KEY` (`uploads/services.ts`, `unassigned.ts`, and
 * both branches of the saga). One rule, matching the write, by construction.
 */
const applyStatusTagWrite = <T>(
  tags: readonly T[],
  statusWriteFields: Parameters<typeof mergeRedactionStatusTagFields>[1]
): T[] => {
  const isStatusTag = (tag: T) =>
    !!tag && (tag as { value?: unknown }).value === IN_REDACTION_TAG_KEY;
  return tags.some(isStatusTag)
    ? tags.map((tag) =>
        isStatusTag(tag)
          ? (mergeRedactionStatusTagFields(
              tag as object,
              statusWriteFields
            ) as T)
          : tag
      )
    : [...tags, { value: IN_REDACTION_TAG_KEY, ...statusWriteFields } as T];
};

/**
 * VE-26943: apply a redaction-status tag write to the enriched TDO behind an
 * unassigned file. No-op when the tdo isn't in the unassigned list, so the
 * ordinary case-file path costs one lookup and nothing else.
 */
const updateLegacyTdoStatusTag = (
  legacyTdoDetails: CaseMediaState['legacyTdoDetails'],
  tdoId: TDOId,
  statusWriteFields: Parameters<typeof mergeRedactionStatusTagFields>[1]
): CaseMediaState['legacyTdoDetails'] => {
  const key = `id_${tdoId}`;
  const record = legacyTdoDetails?.[key];
  if (!record) {
    return legacyTdoDetails;
  }
  const details = record.details ?? {};
  const tags = (details.tags ?? []) as (Record<string, unknown> | null)[];
  const updatedTags = applyStatusTagWrite(tags, statusWriteFields);
  return {
    ...legacyTdoDetails,
    [key]: { ...record, details: { ...details, tags: updatedTags } },
  };
};

const fetchUserOrganizationSuccess = (
  state: CaseMediaState,
  action: Action<OrganizationPayload, object | undefined>
) => {
  const applicationIds =
    action?.payload?.me?.organization?.jsondata?.applicationIds;
  const isIlluminateAppExist =
    applicationIds?.length > 0 && applicationIds.includes(ILLUMINATE_APP_ID);

  return isIlluminateAppExist
    ? { ...state, isIlluminateAppEnabled: isIlluminateAppExist }
    : state;
};

const setSourceInputFileToMove = (
  state: CaseMediaState,
  action: {
    payload: ActionSetMoveInputFilePayload;
  }
) => {
  const { sourceInputFileToMove, moveFromFolder } = action.payload;
  return {
    ...state,
    sourceInputFileToMove: sourceInputFileToMove,
    moveFromFolder: moveFromFolder,
  };
};

const changeFileFilters = (
  state: CaseMediaState,
  action: {
    payload: ActionChangeFileFiltersPayload;
  }
) => ({
  ...state,
  filters: {
    ...state.filters,
    ...action.payload,
  },
});

const changeFileViewMode = (
  state: CaseMediaState,
  action: {
    payload: FileViewMode;
  }
) =>
  // VE-26943: while Unassigned Media is open the toggle writes THAT folder's
  // preference, so switching to Details there neither leaks into real requests
  // nor gets overwritten by this folder's Grid default on re-entry.
  (state.selectedFolders || []).some((folder) =>
    isUnassignedFolderId(folder?.id)
  )
    ? { ...state, unassignedFileViewMode: action.payload }
    : { ...state, fileViewMode: action.payload };

/**
 * VE-32420: Unassigned Media's sort. Only the value is written here — the
 * re-read it implies is a side effect, so it lives in the saga.
 */
const setUnassignedSort = (
  state: CaseMediaState,
  action: { payload: UnassignedSort }
): CaseMediaState => ({
  ...state,
  unassignedSort: action.payload,
  // The saga re-reads from offset 0, so the read in flight is over. Resetting
  // here means a page still coming from it cannot pass the continuation check
  // above, whichever order the two responses happen to land in.
  fetchMoreLegacyMediasOffset: 0,
});

const setSidebarCollapsed = (
  state: CaseMediaState,
  action: {
    payload: boolean;
  }
) =>
  // Same split as changeFileViewMode: Unassigned Media defaults to collapsed
  // and every other request to expanded, so they cannot share one value.
  (state.selectedFolders || []).some((folder) =>
    isUnassignedFolderId(folder?.id)
  )
    ? { ...state, isUnassignedSidebarCollapsed: action.payload }
    : { ...state, isPanelSidebarCollapsed: action.payload };

const setFolderTreeFetchState =
  (folderTreeFetchState: CaseMediaState['folderTreeFetchState']) =>
  (state: CaseMediaState) => ({
    ...state,
    folderTreeFetchState,
  });

const setDownloadAllForTdo = (
  state: CaseMediaState,
  action: {
    payload: ActionDownloadAllForTdoPayload;
  }
): CaseMediaState => {
  state.selectedFolders = [
    ...(state.selectedFolders || []).map((folder) => {
      if (folder.treeObjectId === action.payload?.treeObjectId) {
        folder.caseFiles = {
          ...folder.caseFiles,
          inputFiles: folder.caseFiles?.inputFiles?.map((file) => {
            if (file.id === action.payload.tdoId) {
              return {
                ...file,
                downloadAllStarted: action.payload.status,
              };
            }
            return file;
          }),
        };
      }
      return folder;
    }),
  ];
  return state;
};

export const caseMediaReducer = {
  [namespace]: createReducer(defaultState, (builder) => {
    builder
      .addCase(FETCH_ROOT_FOLDERS_INIT, fetchRootFolderInit)
      .addCase(FETCH_ROOT_FOLDERS_SUCCESS, fetchRootFolderSuccess)
      .addCase(FETCH_ROOT_FOLDERS_FAILURE, fetchRootFolderFailure)
      .addCase(SET_ROOT_FOLDER_SMART_SELECTION, setRootFolderSmartSelection)
      .addCase(CREATE_ROOT_FOLDER_FAILURE, fetchRootFolderFailure)
      .addCase(SET_SELECTED_FOLDER_REQUEST, setSelectedFolderRequest)
      .addCase(FETCH_SUB_FOLDERS_BYID_REQUEST, fetchSubFoldersByIdRequest)
      .addCase(FETCH_SUB_FOLDERS_BYID_SUCCESS, fetchSubFoldersByIdSuccess)
      .addCase(FETCH_SUB_FOLDERS_BYID_FAILURE, fetchSubFoldersByIdFailure)
      .addCase(
        SET_SELECTED_VIRTUAL_FOLDER_REQUEST,
        setSelectedVirtualFolderRequest
      )
      .addCase(DELETE_FOLDER_INIT, deleteFolderInit)
      .addCase(DELETE_FOLDER_SUCCESS, deleteFolderSuccess)
      .addCase(DELETE_FOLDER_FAILURE, deleteFolderFailure)
      .addCase(SOFT_DELETE_FOLDER_SUCCESS, softDeleteFolderSuccess)
      .addCase(SOFT_DELETE_FOLDER_FAILURE, softDeleteFolderFailure)
      .addCase(DELETE_FILE_INIT, deleteFileInit)
      .addCase(DELETE_FILE_SUCCESS, deleteFileSuccess)
      .addCase(DELETE_FILE_FAILURE, deleteFileFailure)
      .addCase(FETCH_EXPORT_TDO_SUCCESS, fetchExportTdoSuccess)
      .addCase(SET_DOWNLOAD_ALL_STATUS, setDownloadAllStatus)
      .addCase(FETCH_MORE_ROOTFOLDERS_INIT, fetchRootFolderInit)
      .addCase(FETCH_MORE_ROOTFOLDERS_SUCCESS, fetchMoreRootFoldersSuccess)
      .addCase(FETCH_MORE_ROOTFOLDERS_FAILURE, fetchRootFolderFailure)
      .addCase(FETCH_MORE_SUBFOLDERS_INIT, fetchSubFoldersByIdRequest)
      .addCase(FETCH_MORE_SUBFOLDERS_SUCCESS, fetchMoreSubFoldersSuccess)
      .addCase(FETCH_MORE_SUBFOLDERS_FAILURE, fetchSubFoldersByIdFailure)
      .addCase(MOVE_FOLDER_SUCCESS, moveFolderSuccess)
      .addCase(REMOVE_SELECTED_FOLDER, removeSelectedFolder)
      .addCase(
        FETCH_SELECTED_CASE_OR_FOLDER_TREE_SUCCESS,
        fetchCaseOrFolderTreeSuccess
      )
      .addCase(ROUTE_HOME, setNavToCaseMedia)
      .addCase(CLEAR_NAV_CASE, clearNavCase)
      .addCase(SET_EDITOR_RETURN_TARGET, setEditorReturnTarget)
      .addCase(MARK_FOLDER_VIEW_LEFT, markFolderViewLeft)
      .addCase(CLEAR_ALL_SELECTED_FOLDERS, clearAllSelectedFolders)
      .addCase(SET_LAST_ACTIVE_FOLDER_VIEW_FILE, setLastActiveFolderViewFile)
      .addCase(MARK_TDO_RECORDED, markTdoRecorded)
      .addCase(MARK_FOLDER_VIEW_TDO_OPENED, markFolderViewTdoOpened)
      .addCase(CONSUME_FOLDER_VIEW_RETURN_SCROLL, consumeFolderViewReturnScroll)
      .addCase(
        CLEAR_LAST_ACTIVE_FOLDER_VIEW_FILE,
        clearLastActiveFolderViewFile
      )
      .addCase(
        CLEAR_LAST_OPENED_FOLDER_VIEW_FILE_FOR_CASE,
        clearLastOpenedFolderViewFileForCase
      )
      .addCase(SET_SEARCH_TEXT, setSearchTextReducer)
      .addCase(SEARCH_CASES_AND_FOLDERS_INIT, searchCasesAndFoldersInit)
      .addCase(SEARCH_CASES_AND_FOLDERS_FAILURE, searchCasesAndFoldersFailure)
      .addCase(SET_SEARCH_RESULTS, setSearchResultsReducer)
      .addCase(RESTORE_SEARCH_RESULTS, restoreSearchResultsReducer)
      .addCase(CLEAR_ALL_SEARCH_RESULTS, clearAllSearchResults)
      .addCase(SET_SEARCH_MORE_RESULTS, setSearchMoreResults)
      .addCase(SET_SEARCH_SCOPE, setSearchScope)
      .addCase(CLEAR_SEARCH_SCOPE, clearSearchScope)
      .addCase(SET_SEARCH_BAR_SCOPE_MODE, setSearchBarScopeMode)
      .addCase(OPEN_SEARCH_DIALOG, openSearchDialog)
      .addCase(CLOSE_SEARCH_DIALOG, closeSearchDialog)
      .addCase(FETCH_MORE_FILES_INIT, fetchMoreFilesInit)
      .addCase(FETCH_MORE_FILES_SUCCESS, fetchMoreFilesSuccess)
      .addCase(FETCH_MORE_FILES_FAILURE, fetchMoreFilesFailure)
      .addCase(SET_SHOW_LEGACY_MEDIAS, setShowLegacyMedias)
      .addCase(FETCH_LEGACY_MEDIAS_INIT, fetchLegacyMediasInit)
      .addCase(FETCH_LEGACY_MEDIAS_SUCCESS, fetchLegacyMediasSuccess)
      .addCase(FETCH_LEGACY_MEDIAS_FAILURE, fetchLegacyMediasFailure)
      .addCase(FETCH_TDO_DETAIL_FOR_SDOS_SUCCESS, fetchTdoDetailForSdosSuccess)
      .addCase(REFRESH_FILE_OUTPUTS_SUCCESS, refreshFileOutputsSuccess)
      .addCase(EXPORT_COMPLETED, redactedDocumentExported)
      .addCase(DELETE_REDACTED_DOCUMENT_SUCCESS, redactedDocumentDeleted)
      .addCase(SAVE_SUCCESS, redactionDataSaved)
      .addCase(RENAME_DOCUMENT_SUCCESS, documentRenamed)
      .addCase(FETCH_TDO_DETAIL_FOR_SDOS_FAILURE, fetchTdoDetailForSdosFailure)
      .addCase(RENAME_FOLDER_SUCCESS, renameFolderSuccess)
      .addCase(SET_MOVE_EXPANDED_FOLDER_REQUEST, setMoveExpandedFolderRequest)
      .addCase(SET_SOURCE_FOLDER_TO_MOVE_REQUEST, setSourceFolderToMoveRequest)
      .addCase(CLEAR_ALL_MOVE_FOLDER_STATE_REQUEST, clearAllMoveStateRequest)
      .addCase(CLEAR_FROM_MOVE_EXPANDED_REQUEST, clearFromMoveExpandedRequest)
      .addCase(FETCH_FOR_EXPANDED_FOLDER_INIT, fetchForExpandedFolderInit)
      .addCase(FETCH_FOR_EXPANDED_FOLDER_SUCCESS, fetchForExpandedFolderSuccess)
      .addCase(FETCH_FOR_EXPANDED_FOLDER_FAILURE, fetchForExpandedFolderFailure)
      .addCase(DELETE_MEDIA_FILE_SDO_SUCCESS, (state, action) => {
        // VE-26943: the SDO is what puts a file in this folder, so deleting it
        // (hard delete, or a move into a request) removes the row. In place,
        // not by refetching — see removeUnassignedFileInPlace.
        const sdoId = (action as { payload?: { sdoId?: string } }).payload
          ?.sdoId;
        if (sdoId) {
          removeUnassignedFileInPlace(state, (m) => m.sdoId === sdoId);
        }
      })
      .addCase(SET_SOURCE_FILE_TO_MOVE_REQUEST, setSourceFileToMoveRequest)
      .addCase(FILE_TDO_WITH_CASE_SUCCESS, clearSourceFileToMove)
      .addCase(SET_SELECTED_REQUEST_TO_INGEST, setSelectedRequestToIngest)
      .addCase(CLEAR_SELECTED_REQUEST_TO_INGEST, clearSelectedRequestToIngest)
      .addCase(SET_CASE_MEDIA_STATUS, setCaseMediaStatus)
      .addCase(UPDATE_REDACTION_STATUS_CHANGE, updateStatusChangeSuccess)
      .addCase(REQUEST_USER_ORGANIZATION_SUCCESS, fetchUserOrganizationSuccess)
      .addCase(SET_SOURCE_INPUT_FILE_TO_MOVE, setSourceInputFileToMove)
      .addCase(CHANGE_FILE_FILTERS, changeFileFilters)
      .addCase(SET_UNASSIGNED_SORT, setUnassignedSort)
      .addCase(CHANGE_FILE_VIEW_MODE, changeFileViewMode)
      .addCase(SET_SIDEBAR_COLLAPSED, setSidebarCollapsed)
      .addCase(
        FETCH_SELECTED_CASE_OR_FOLDER_TREE_INIT,
        setFolderTreeFetchState('fetching')
      )
      // 'succeeded' is set inside fetchCaseOrFolderTreeSuccess (the action's
      // existing handler above), atomically with the selection it applies.
      .addCase(
        FETCH_SELECTED_CASE_OR_FOLDER_TREE_FAILURE,
        setFolderTreeFetchState('failed')
      )
      .addCase(SET_DOWNLOAD_ALL_FOR_TDO, setDownloadAllForTdo)
      .addCase(TOGGLE_FILE_MULTI_SELECT, toggleFileMultiSelect)
      .addCase(SELECT_FILE_MULTI_RANGE, selectFileMultiRange)
      .addCase(CLEAR_FILE_MULTI_SELECT, clearFileMultiSelect)
      .addCase(SET_PENDING_REMOVAL_FILES, setPendingRemovalFiles)
      .addCase(RELEASE_PENDING_REMOVAL_FILES, releasePendingRemovalFiles)
      .addCase(CLEAR_PENDING_REMOVAL_FILES, clearPendingRemovalFiles)
      .addCase(RELEASE_PENDING_TRASH_FOLDERS, releasePendingTrashFolders)
      .addCase(REMOVE_CASE_INPUT_FILES, removeCaseInputFiles)
      .addCase(SET_BATCH_OPERATION, setBatchOperation)
      .addCase(SET_FOLDER_PERMISSIONS, setFolderPermissions)
      .addCase(FETCH_REQUEST_ROSTER_REQUEST, fetchRequestRosterRequest)
      .addCase(SET_REQUEST_ROSTER, setRequestRoster)
      .addCase(PUBLISH_REQUEST_ROSTER, publishRequestRoster)
      .addCase(APPEND_REQUEST_ROSTER, appendRequestRoster)
      .addCase(MERGE_CASE_FILE_ROWS, mergeCaseFileRows)
      .addCase(CREATE_PROJECT_SUCCESS, recordProjectPointers)
      // Delete carries the list AFTER removal, so one handler serves both.
      .addCase(DELETE_PROJECT_SUCCESS, recordProjectPointers);
  }),
};
